// Repair Point blog posts.
// Each post is composed of structured "blocks" that render as semantic HTML,
// which keeps Google's crawlers happy and the visual style consistent.
//
// Block types: h2, p, ul, ol, callout, quote, cta.

export const POSTS = [
  {
    slug: "how-to-know-if-your-pc-has-a-virus",
    title: "How to Know if Your PC Has a Virus: 9 Warning Signs",
    description:
      "Suspect your Windows PC is infected? Here are nine clear warning signs of a virus, ransomware or hidden malware — plus what to do next (without falling for fake scareware).",
    cover:
      "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A glowing red warning icon on a darkened laptop screen",
    date: "2026-02-04",
    readTime: 7,
    tags: ["Windows", "Security", "Virus removal"],
    relatedService: "virus-removal",
    blocks: [
      {
        type: "p",
        text: "Modern malware is quieter than it used to be. Instead of locking your screen with a red banner, it hides in the background — slowing your PC, stealing credentials and sometimes encrypting files for ransom. Here are the nine signs we look for every week at the Repair Point workshop.",
      },
      { type: "h2", text: "1. Your PC is suddenly slow — but only sometimes" },
      {
        type: "p",
        text: "Hidden miners and trojans use spare CPU cycles. If your laptop fan spins up while you're just reading email, open Task Manager (Ctrl+Shift+Esc) and look for an unfamiliar process pinning the CPU. A clean Windows 11 install should idle below 5%.",
      },
      { type: "h2", text: "2. Pop-ups appear outside the browser" },
      {
        type: "p",
        text: "Browser pop-ups are normal. Pop-ups that appear when no browser is open — particularly ones offering antivirus, gift cards, or 'system warnings' — are almost always malware.",
      },
      { type: "h2", text: "3. Your homepage or search engine has changed" },
      {
        type: "p",
        text: "Browser hijackers redirect your default search engine to ad-heavy sites. Resetting Chrome or Edge usually fixes the symptom but rarely removes the underlying extension.",
      },
      { type: "h2", text: "4. Programs crash for no reason" },
      {
        type: "p",
        text: "Malware injects itself into legitimate processes. The result is random crashes in apps that worked yesterday — especially browsers, Outlook and Office.",
      },
      { type: "h2", text: "5. You see new toolbars, extensions or shortcuts" },
      {
        type: "p",
        text: "Open Settings → Apps → Installed apps and Chrome → Extensions. Anything you don't remember installing should be removed (or investigated).",
      },
      { type: "h2", text: "6. Files have weird extensions or won't open" },
      {
        type: "p",
        text: "If your photos suddenly end in .locked, .encrypted or .[random] — stop using the computer. This is a ransomware infection and continued use can destroy recoverable backups.",
      },
      {
        type: "callout",
        title: "Don't pay the ransom.",
        text: "Less than 30% of ransom payments result in working decryption. Disconnect from the network and call a qualified technician immediately.",
      },
      { type: "h2", text: "7. Your friends report strange messages from you" },
      {
        type: "p",
        text: "Email or social-media account takeovers often start on the device. If contacts say they got odd links from your address, change the password from a clean device first, then disinfect the PC.",
      },
      { type: "h2", text: "8. Windows Defender has been turned off" },
      {
        type: "p",
        text: "Most malware disables Defender silently. Open Windows Security → Virus & threat protection. If real-time protection is off and you didn't disable it, treat the system as infected.",
      },
      { type: "h2", text: "9. Your internet feels off" },
      {
        type: "p",
        text: "Unexpected DNS changes, blocked banking sites, or certificate warnings on sites that previously worked are all signs of network-level malware.",
      },
      { type: "h2", text: "What to do next" },
      {
        type: "ol",
        items: [
          "Disconnect from Wi-Fi and Ethernet to stop data exfiltration.",
          "Do not enter any banking or work passwords on the device.",
          "Back up critical documents to an external drive only if you're sure they're not encrypted.",
          "Run a full scan with Windows Defender Offline (Settings → Windows Security → Virus & threat protection → Scan options).",
          "If anything is found — or symptoms persist — get a professional clean.",
        ],
      },
      {
        type: "callout",
        title: "Avoid 'free virus removal' phone calls.",
        text: "Microsoft will never cold-call you about your PC. If someone calls claiming your computer is infected, hang up. Genuine repair shops (including Repair Point) only respond when you contact us first.",
      },
      {
        type: "cta",
        text: "Worried your PC is infected? Book a free written diagnostic — flat-rate $99 full virus removal if needed.",
      },
    ],
  },

  {
    slug: "mac-running-slow-7-fixes",
    title: "Mac Running Slow? 7 Fixes That Actually Work",
    description:
      "From beachballs to never-ending Spotlight indexing — here are the seven proven fixes our macOS technicians use before recommending a full reinstall.",
    cover:
      "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A frustrated user looking at a slow MacBook",
    date: "2026-02-12",
    readTime: 6,
    tags: ["macOS", "Performance"],
    relatedService: "mac-cleanup",
    blocks: [
      {
        type: "p",
        text: "Macs age more gracefully than most laptops, but four to five years in even an M-series MacBook can start to crawl. Here are the seven fixes we run, in order, at the Repair Point bench.",
      },
      { type: "h2", text: "1. Check your free disk space" },
      {
        type: "p",
        text: "macOS slows dramatically below 10% free storage. Open Apple Menu → About This Mac → Storage. Aim for at least 20 GB free. Empty the Trash, clear the Downloads folder and offload large iMovie projects to an external drive.",
      },
      { type: "h2", text: "2. Tame login items" },
      {
        type: "p",
        text: "System Settings → General → Login Items. Disable anything you don't need at startup — Dropbox, Adobe Creative Cloud, Spotify and Zoom are common culprits.",
      },
      { type: "h2", text: "3. Restart properly" },
      {
        type: "p",
        text: "If you only close the lid, your Mac never fully reboots. A real restart (Apple Menu → Restart) clears RAM, kernel cache and stale daemons. Schedule one weekly.",
      },
      { type: "h2", text: "4. Check Activity Monitor" },
      {
        type: "p",
        text: "Open Spotlight and search 'Activity Monitor'. Sort by % CPU. Persistent high CPU from a process you don't recognise is worth investigating — paste the name into a search engine before killing it.",
      },
      { type: "h2", text: "5. Update macOS" },
      {
        type: "p",
        text: "Outdated macOS versions can have unfixed bugs that cause slowdowns. System Settings → General → Software Update. If your Mac is too old for the latest version, stay on the most recent point release for your model.",
      },
      {
        type: "callout",
        title: "Don't install 'Mac cleaner' apps.",
        text: "Almost every 'Mac optimiser' marketed online is bundleware that slows the system further. macOS already includes everything you need — Storage management, Activity Monitor and Software Update.",
      },
      { type: "h2", text: "6. Reset SMC and NVRAM (Intel Macs only)" },
      {
        type: "p",
        text: "If your Intel Mac is sluggish, has weird fan behaviour or wakes slowly, an SMC/NVRAM reset often fixes it. M-series Macs do this automatically — no manual reset needed.",
      },
      { type: "h2", text: "7. Consider an upgrade" },
      {
        type: "p",
        text: "Intel MacBooks with 8 GB RAM and a 256 GB SSD can be transformed with a clean reinstall and storage offloading. If your Mac is older than 2017, however, upgrading the device often makes more sense than further repair spend.",
      },
      { type: "h2", text: "Still slow?" },
      {
        type: "p",
        text: "A clean macOS reinstall with full data migration takes us about three hours and is a $89 flat-rate at Repair Point. We do this every week and back it with a 30-day workmanship warranty.",
      },
      {
        type: "cta",
        text: "Get a written diagnostic on your Mac — we'll tell you honestly whether a tune-up or a new device makes more sense.",
      },
    ],
  },

  {
    slug: "how-to-choose-the-right-antivirus",
    title: "How to Choose the Right Antivirus in 2026 — A Vendor-Neutral Guide",
    description:
      "Free, paid, built-in or third-party? Our technicians compare what actually matters when picking antivirus software in 2026 — without taking commission from anyone.",
    cover:
      "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A laptop with a security shield graphic overlay",
    date: "2026-02-19",
    readTime: 8,
    tags: ["Security", "Antivirus", "Buying guide"],
    relatedService: "antivirus-setup",
    blocks: [
      {
        type: "p",
        text: "We install, configure and uninstall antivirus software every week — across Windows 10, Windows 11 and macOS. Here is the advice we give customers who ask, without any vendor commission attached.",
      },
      { type: "h2", text: "Start with what's already on your computer" },
      {
        type: "p",
        text: "Windows 11 ships with Microsoft Defender, which scores at or near the top of independent AV-TEST and AV-Comparatives results every quarter. For the vast majority of home users it is enough — provided it is on, kept updated, and paired with a good browser and password manager.",
      },
      { type: "h2", text: "Three questions to ask yourself" },
      {
        type: "ol",
        items: [
          "Do I do online banking from this device? — Yes ⇒ a paid suite with a hardened browser is worth it.",
          "Do children or guests use this device? — Yes ⇒ web filtering and content controls matter.",
          "Do I run my business from this device? — Yes ⇒ choose a product with endpoint detection (EDR), not just signature scanning.",
        ],
      },
      { type: "h2", text: "What to look for" },
      {
        type: "ul",
        items: [
          "Excellent real-time protection scores (AV-TEST ≥ 5.5/6)",
          "Low false-positive rate (avoid products that block legitimate apps)",
          "Light system impact (you want speed, not slowness)",
          "Clear, no-renewal-trickery pricing",
          "A simple uninstall process",
        ],
      },
      { type: "h2", text: "What to avoid in 2026" },
      {
        type: "ul",
        items: [
          "Bundled 'PC optimisers' and 'driver updaters' — they cause more problems than they fix",
          "Free antivirus that nags constantly to upgrade",
          "Suites that hijack your browser DNS",
          "Subscriptions that auto-renew at 3× the introductory price",
        ],
      },
      {
        type: "callout",
        title: "Two antivirus apps = no antivirus.",
        text: "Running more than one real-time scanner causes them to fight each other and slow Windows to a crawl. Pick one and remove the rest cleanly.",
      },
      { type: "h2", text: "Mac users — do you need antivirus?" },
      {
        type: "p",
        text: "macOS has built-in XProtect and Gatekeeper. For most users that's sufficient. If you handle sensitive client data, or you regularly download from untrusted sources, a lightweight tool from a reputable vendor adds a useful second layer.",
      },
      { type: "h2", text: "When to call a technician" },
      {
        type: "p",
        text: "Removing an old antivirus is often messier than installing a new one. Norton, McAfee and Webroot in particular leave services behind that block other apps. A 30-minute clean install and uninstall session ensures your machine is genuinely protected and runs fast.",
      },
      {
        type: "cta",
        text: "Need help picking the right antivirus? Our $69 flat-rate Antivirus Installation, Consultation & Removal service includes vendor-neutral advice and a clean install — done same day.",
      },
    ],
  },

  {
    slug: "why-is-my-pc-so-slow",
    title: "Why Is My PC So Slow? The Real Causes (and How to Fix Them)",
    description:
      "Not every slow PC has a virus. Here are the five most common causes of Windows slowdown — from low free space to a dying SSD — and what you can do about each.",
    cover:
      "https://images.unsplash.com/photo-1587202372775-e229f172b9d7?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A close-up of a laptop motherboard and SSD",
    date: "2026-02-26",
    readTime: 6,
    tags: ["Windows", "Performance"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "About half the 'slow PC' tickets we get aren't malware at all — they're hardware fatigue, bloat or background services. Here are the real causes, in the order we check them.",
      },
      { type: "h2", text: "1. Your SSD is full (or dying)" },
      {
        type: "p",
        text: "SSDs slow down dramatically when they're more than 85% full. Worse, consumer SSDs older than five years can start producing read errors that look like slowdowns. CrystalDiskInfo (free) reports health in 30 seconds.",
      },
      { type: "h2", text: "2. Too many startup apps" },
      {
        type: "p",
        text: "Ctrl+Shift+Esc → Startup apps. Disable anything with 'High' impact you don't strictly need on boot. Adobe, Spotify, Steam and Teams are common offenders.",
      },
      { type: "h2", text: "3. Pending Windows updates" },
      {
        type: "p",
        text: "A PC that has fallen 6+ months behind on Windows updates often runs background catch-up tasks for hours. Force an update cycle, install everything, then reboot.",
      },
      { type: "h2", text: "4. Visual effects on weaker GPUs" },
      {
        type: "p",
        text: "Settings → Accessibility → Visual effects → turn off Transparency and Animation. On low-end laptops this single change can feel like a hardware upgrade.",
      },
      { type: "h2", text: "5. Heat throttling" },
      {
        type: "p",
        text: "Laptops with three+ years of dust in the cooling vents will thermal-throttle — meaning the CPU intentionally slows itself down to avoid overheating. A workshop fan clean is $49 at Repair Point and fixes more 'slow PC' tickets than any software change.",
      },
      {
        type: "callout",
        title: "Quick test:",
        text: "Run Task Manager → Performance → CPU. If the speed never goes above 1.0 GHz under load, your machine is thermal-throttling and needs a physical service, not a software fix.",
      },
      { type: "h2", text: "When a tune-up is the right call" },
      {
        type: "p",
        text: "If you've done all of the above and the machine still feels sluggish, a professional tune-up (registry repair, SFC, DISM, driver audit, fan clean, Windows in-place upgrade) is the next step. We do this for $79 flat and it reliably adds 2–3 years to most laptops.",
      },
      {
        type: "cta",
        text: "Book a $79 flat-rate PC tune-up — typical turnaround 60 minutes, with a free written diagnostic first.",
      },
    ],
  },

  {
    slug: "lost-data-what-to-do-next",
    title: "Lost Data on Your Hard Drive? Here's Exactly What to Do Next",
    description:
      "The first hour after data loss decides whether your files come back. Here's the calm, technician-approved checklist to maximise your chances of full recovery.",
    cover:
      "https://images.unsplash.com/photo-1597852074816-d933c7d2b988?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "An open laptop with a hard drive being inspected",
    date: "2026-03-05",
    readTime: 5,
    tags: ["Data recovery", "Backup"],
    relatedService: "data-recovery",
    blocks: [
      {
        type: "p",
        text: "Whether you accidentally formatted a drive, watched a 'No Bootable Device' error appear, or heard a click-of-death from an external HDD — what you do in the next hour matters more than which company you call. Here is the same checklist we follow at our workshop.",
      },
      { type: "h2", text: "1. Stop using the device — immediately" },
      {
        type: "p",
        text: "Every new write on a drive risks overwriting the very sectors holding your deleted data. Power the device off if you can, and unplug external drives.",
      },
      { type: "h2", text: "2. Don't run recovery software on the original drive" },
      {
        type: "p",
        text: "Free tools like Recuva or PhotoRec are excellent — but only when installed on a different drive. Installing them on the affected disk overwrites the very data you're trying to save.",
      },
      { type: "h2", text: "3. Identify which type of failure you have" },
      {
        type: "ul",
        items: [
          "Logical (accidentally deleted, formatted, file system corruption) — best DIY chance, software can usually help.",
          "Light physical (drive mounts intermittently, slow, SMART warnings) — needs a clone first, then recovery from the clone.",
          "Heavy physical (clicking, not detected, won't spin) — DIY will make it worse. Stop and call a professional.",
        ],
      },
      {
        type: "callout",
        title: "Click-of-death = stop.",
        text: "A clicking hard drive has mechanical damage. Every additional power-on attempt risks total platter scoring. Power off, package the drive in an anti-static bag, and bring it in.",
      },
      { type: "h2", text: "4. Don't trust 'free recovery' chains" },
      {
        type: "p",
        text: "Reputable recovery services will quote in writing with a no-data-no-fee guarantee. If a shop quotes blind, or asks for payment upfront before opening the drive, walk away.",
      },
      { type: "h2", text: "5. After recovery — set up a 3-2-1 backup" },
      {
        type: "p",
        text: "Three copies of every important file, on two different media types, with one off-site. Cloud backup (iCloud, OneDrive, Google Drive, Backblaze) counts as the off-site copy and costs less than a coffee a week.",
      },
      {
        type: "cta",
        text: "Need professional data recovery? We offer no-data-no-fee recovery from $149 with a free written assessment.",
      },
    ],
  },

  {
    slug: "blue-screen-of-death-bsod-explained",
    title: "Blue Screen of Death (BSOD): What It Means and How to Fix It",
    description:
      "What that frozen blue Windows screen is actually telling you, the seven most common stop codes, and the diagnostic steps our technicians run before reaching for a reinstall.",
    cover:
      "https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A close-up of a laptop screen showing a Windows error",
    date: "2026-03-12",
    readTime: 7,
    tags: ["Windows", "Troubleshooting"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "A Blue Screen of Death — properly called a Stop Error — is Windows' way of preventing data loss when something has gone seriously wrong at the kernel level. Modern BSODs are rarely fatal; they're a diagnostic clue, not a death sentence. Here is how we read them at the Repair Point bench.",
      },
      { type: "h2", text: "Step 1: Read the stop code" },
      {
        type: "p",
        text: "Every BSOD includes a short stop code at the bottom (e.g. CRITICAL_PROCESS_DIED, MEMORY_MANAGEMENT). Take a photo with your phone before restarting — the code tells us which subsystem failed.",
      },
      { type: "h2", text: "Step 2: Did it happen once or is it repeating?" },
      {
        type: "p",
        text: "A single BSOD after a Windows update or driver install is normal. Two or more in a week is a real fault. Repeating BSODs at the same task (e.g. loading Chrome) usually point to software; random BSODs usually point to hardware.",
      },
      { type: "h2", text: "The 7 stop codes we see most" },
      {
        type: "ul",
        items: [
          "MEMORY_MANAGEMENT — almost always faulty RAM. Run Windows Memory Diagnostic.",
          "CRITICAL_PROCESS_DIED — corrupted system file. Boot into Safe Mode and run sfc /scannow.",
          "PAGE_FAULT_IN_NONPAGED_AREA — driver or RAM fault. Roll back the most recent driver.",
          "IRQL_NOT_LESS_OR_EQUAL — a driver tried an illegal memory access. Update or remove third-party drivers.",
          "SYSTEM_THREAD_EXCEPTION_NOT_HANDLED — usually a graphics driver. Clean-install GPU drivers via DDU.",
          "KERNEL_SECURITY_CHECK_FAILURE — disk corruption. Run chkdsk /f /r on the system drive.",
          "INACCESSIBLE_BOOT_DEVICE — storage controller or failing SSD. Stop and back up immediately.",
        ],
      },
      {
        type: "callout",
        title: "If you see INACCESSIBLE_BOOT_DEVICE, stop using the machine.",
        text: "This one usually means the SSD is failing. Every reboot risks unrecoverable data loss. Power off and bring the device in for a clone-and-recover.",
      },
      { type: "h2", text: "DIY checklist before calling us" },
      {
        type: "ol",
        items: [
          "Photo the stop code.",
          "Boot into Safe Mode (hold Shift while clicking Restart).",
          "Roll back any driver or update installed in the last 7 days (Device Manager → Driver tab → Roll Back).",
          "Run: sfc /scannow then DISM /Online /Cleanup-Image /RestoreHealth.",
          "Run Windows Memory Diagnostic (mdsched.exe). One pass takes ~20 minutes.",
          "Check Event Viewer → Windows Logs → System for red 'Critical' entries around the BSOD time.",
        ],
      },
      { type: "h2", text: "When DIY won't fix it" },
      {
        type: "p",
        text: "If sfc reports unrepairable corruption, if Memory Diagnostic finds errors, or if BSODs continue after a clean Windows install — you're looking at a hardware fault (RAM, SSD or motherboard). A $49 written diagnostic at our workshop pinpoints the exact part and gives you a flat-rate repair quote on the spot.",
      },
      {
        type: "cta",
        text: "Stuck in a BSOD loop? Book a $49 written diagnostic — we'll tell you within an hour whether it's a 60-minute software fix or a hardware repair.",
      },
    ],
  },

  {
    slug: "how-to-back-up-your-pc-properly-3-2-1-rule",
    title: "How to Back Up Your PC Properly: The 3-2-1 Rule Explained (2026)",
    description:
      "The single backup strategy used by every serious IT team — explained in plain English, with the exact tools we recommend for Windows and macOS in 2026.",
    cover:
      "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "An external hard drive plugged into a laptop on a wooden desk",
    date: "2026-03-19",
    readTime: 6,
    tags: ["Backup", "Data recovery", "Security"],
    relatedService: "data-recovery",
    blocks: [
      {
        type: "p",
        text: "Hard drives fail. Laptops are stolen. Houses flood. The only question is whether you'll be ready when it happens. The 3-2-1 backup rule is the simplest, most reliable answer — and it has remained the industry standard for over twenty years because nothing better has come along.",
      },
      { type: "h2", text: "What the 3-2-1 rule actually means" },
      {
        type: "ul",
        items: [
          "3 copies of every important file (the original + 2 backups).",
          "2 different storage media (so one type of failure can't kill both backups).",
          "1 off-site copy (so a fire, flood or theft can't take everything at once).",
        ],
      },
      { type: "h2", text: "The simplest real-world setup" },
      {
        type: "p",
        text: "For a typical Windows or Mac user we recommend exactly this stack — it costs less than $5 a month and takes 20 minutes to set up.",
      },
      {
        type: "ol",
        items: [
          "Copy 1 (original): your laptop's internal SSD.",
          "Copy 2 (local backup): a $60 external SSD running Windows File History or macOS Time Machine. Plug in once a week.",
          "Copy 3 (off-site): a cloud backup service — OneDrive, iCloud Drive, Google Drive or Backblaze. Set and forget.",
        ],
      },
      {
        type: "callout",
        title: "Sync is not backup.",
        text: "If you delete a file from OneDrive, it deletes from your laptop too. Cloud sync is great for working across devices but it does not protect against accidental deletion or ransomware. Backblaze ($9/month, unlimited) is true backup with 30-day version history.",
      },
      { type: "h2", text: "Tools we recommend in 2026" },
      {
        type: "ul",
        items: [
          "Windows local backup: Macrium Reflect Free, or built-in File History.",
          "Mac local backup: Time Machine. No alternative needed.",
          "Cloud backup: Backblaze for laptops, IDrive for small businesses.",
          "Photo backup (separately): Google Photos or Apple Photos with iCloud+.",
          "Cloud sync (not backup): OneDrive, Dropbox, iCloud Drive.",
        ],
      },
      { type: "h2", text: "Test the backup. Always." },
      {
        type: "p",
        text: "A backup you've never restored is a hope, not a backup. Once a quarter, pick one document and try to restore it from the cloud and from the external drive. If you can't, fix it before you actually need it.",
      },
      { type: "h2", text: "For small businesses" },
      {
        type: "p",
        text: "Multi-device businesses should add a fourth element: a monthly cold backup to a drive that lives in a locked drawer (or another building). It's the only protection against the rare case where ransomware spreads through your cloud sync as well. We set this up for clients as part of our Fleet Maintenance Plans.",
      },
      {
        type: "cta",
        text: "We'll audit your current backup setup for free in a 20-minute phone call — no obligation. If your data isn't safe, we'll fix it.",
      },
    ],
  },

  {
    slug: "ransomware-2026-prevention-playbook",
    title: "Ransomware in 2026: The Prevention Playbook for Homes & Small Businesses",
    description:
      "Ransomware is bigger business than ever in 2026. Here is the calm, technician-tested checklist we recommend to every Repair Point customer — what to do, what to avoid and how to recover if it happens to you.",
    cover:
      "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A laptop with a lock icon overlay, representing ransomware threats",
    date: "2026-03-26",
    readTime: 7,
    tags: ["Security", "Ransomware", "Backup"],
    relatedService: "virus-removal",
    blocks: [
      {
        type: "p",
        text: "Ransomware is no longer a corporate-only problem. We now see at least one home or small-business case at the Repair Point workshop every week. The good news: 95% of the infections we deal with were preventable with a 30-minute setup. This is exactly what we recommend.",
      },
      { type: "h2", text: "How ransomware really gets in (in 2026)" },
      {
        type: "ul",
        items: [
          "Phishing emails with malicious attachments or links — still #1 by a wide margin.",
          "Cracked or pirated software downloaded from torrent sites.",
          "Compromised browser extensions and Chrome 'profile sync' attacks.",
          "Outdated VPN appliances and routers with default passwords.",
          "Public Wi-Fi sessions where the device's firewall is off.",
        ],
      },
      { type: "h2", text: "The 7-step prevention checklist" },
      {
        type: "ol",
        items: [
          "Patch everything. Set Windows, macOS, your browser and your router to update automatically.",
          "Run only one real-time antivirus (Windows Defender or one paid suite). Two antivirus apps fight each other and create gaps.",
          "Enable controlled folder access (Windows) or FileVault + macOS Gatekeeper (Mac).",
          "Back up to the 3-2-1 rule (3 copies, 2 different media, 1 off-site). See our backup guide.",
          "Use a password manager. Reused passwords are how attackers move from one account to another.",
          "Turn on two-factor authentication on your email — that single account is the key to most other recoveries.",
          "Never download cracked software. The economic value to the cracker is paid with a hidden payload.",
        ],
      },
      {
        type: "callout",
        title: "Email is the front door.",
        text: "Roughly 70% of the ransomware cases we see start with one click on a convincing email. If you're unsure about an email, forward it to us and we'll verify it before you open anything.",
      },
      { type: "h2", text: "If it happens — the first 60 minutes" },
      {
        type: "ol",
        items: [
          "Disconnect from Wi-Fi and Ethernet immediately — stop the spread.",
          "Do NOT pay the ransom. Industry data shows fewer than 30% of payments result in working decryption, and you mark yourself as a future target.",
          "Power the device off if you can, and unplug external drives before they get encrypted too.",
          "Photograph the ransom note. The note often identifies the strain — and some strains have free decryptors.",
          "Call a qualified technician. Free public decryptors exist for many strains (Stop/Djvu, Globe, TeslaCrypt, others) — we check first before recommending a wipe.",
        ],
      },
      { type: "h2", text: "What about cyber-insurance?" },
      {
        type: "p",
        text: "If you run a small business, 2026 insurance policies will only pay out if you can show evidence of basic controls: a working backup, MFA on email, and EDR-grade endpoint protection. A 1-hour audit before you sign the policy will save you a five-figure claim denial later.",
      },
      { type: "h2", text: "Don't panic, don't pay, do call" },
      {
        type: "p",
        text: "Most ransomware infections we handle are fully recovered from clean backups in under a day — without paying a cent. The earlier you call, the lower the cost: every additional reboot can damage shadow copies and pre-encryption sectors that aid recovery.",
      },
      {
        type: "cta",
        text: "Worried you might be infected? Book a free remote diagnostic — we'll tell you within an hour whether it's a real ransomware case, a scareware popup, or something else entirely.",
      },
    ],
  },

  {
    slug: "mac-battery-health-when-to-replace",
    title: "Mac Battery Health: How to Read It, and When to Replace It",
    description:
      "Your MacBook battery dying mid-meeting? Here is how to read your macOS battery report properly, what the numbers actually mean, and when a replacement is worth the cost.",
    cover:
      "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A person working on a MacBook on a wooden table",
    date: "2026-04-02",
    readTime: 5,
    tags: ["macOS", "Hardware", "Battery"],
    relatedService: "hardware",
    blocks: [
      {
        type: "p",
        text: "Apple rates a modern MacBook battery for 1,000 cycles before it should still hold 80% of its original capacity. In reality, we see batteries that fall below that threshold at 600 cycles — and others that comfortably pass 1,200. Here is the honest, technician's view.",
      },
      { type: "h2", text: "Step 1: Read your battery report" },
      {
        type: "p",
        text: "macOS hides the most useful battery stats. Open System Settings → Battery → Battery Health. Click the small (i) icon next to 'Battery Health' and look for two numbers:",
      },
      {
        type: "ul",
        items: [
          "Maximum Capacity — the percentage of original capacity remaining. Apple replaces under warranty if this drops below 80% within 1,000 cycles.",
          "Cycle Count — how many full charge cycles your battery has done. View it in System Information → Power.",
        ],
      },
      { type: "h2", text: "Step 2: Interpret the numbers" },
      {
        type: "ol",
        items: [
          "Maximum Capacity above 85% — battery is healthy, no action needed.",
          "Maximum Capacity 80–85% — you'll notice slightly shorter sessions; not yet a hardware fault.",
          "Maximum Capacity below 80% — macOS will eventually show a 'Service Recommended' message. Replacement is justified.",
          "Maximum Capacity below 60% — the laptop will shut down unexpectedly under load. Replace soon.",
        ],
      },
      {
        type: "callout",
        title: "Cycle count alone tells you nothing.",
        text: "Two MacBooks with identical 800-cycle counts can have very different capacities — depends on charging habits, heat exposure and storage history. Always read Maximum Capacity, not cycle count.",
      },
      { type: "h2", text: "Tips to extend battery life" },
      {
        type: "ul",
        items: [
          "Enable Optimised Battery Charging — caps charging at 80% when macOS detects you don't need a full charge.",
          "Keep your Mac out of hot cars. Heat above 35 °C accelerates degradation faster than any other factor.",
          "Don't store a fully discharged Mac for months. Aim for 50–60% if you're putting it away.",
          "Avoid leaving the laptop plugged in 100% for weeks on end. Apple's optimised charging mostly handles this, but unplug occasionally.",
        ],
      },
      { type: "h2", text: "When replacement makes sense" },
      {
        type: "p",
        text: "A genuine OEM-quality MacBook battery replacement costs $99–$179 at Repair Point depending on model — a fraction of a new MacBook. We use Apple-spec cells, recalibrate the controller, and back it with a 6-month warranty. For 2017+ MacBooks that are otherwise fine, this is consistently the highest-value upgrade you can do.",
      },
      { type: "h2", text: "When it doesn't" },
      {
        type: "p",
        text: "If your MacBook is pre-2016 and already feels slow, the battery is the third or fourth problem on its list. We'll tell you honestly during the diagnostic if a new device is the smarter spend.",
      },
      {
        type: "cta",
        text: "Send us your Mac's model and Battery Health number and we'll quote you a flat-rate replacement in writing — no pressure, no upsell.",
      },
    ],
  },

  {
    slug: "how-to-tell-if-your-ssd-is-dying",
    title: "How to Tell If Your SSD Is Dying: 5 Warning Signs You Can't Ignore",
    description:
      "Unlike old spinning hard drives, dying SSDs rarely make noise — they just fail. Here are the five symptoms our technicians watch for, and the free tools that confirm whether yours has weeks or months left.",
    cover:
      "https://images.unsplash.com/photo-1551739440-5dd934d3a94a?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A close-up of a laptop SSD on a workshop bench",
    date: "2026-04-09",
    readTime: 6,
    tags: ["Hardware", "Storage", "Windows"],
    relatedService: "hardware",
    blocks: [
      {
        type: "p",
        text: "Modern SSDs are far more reliable than the spinning drives they replaced — but they fail differently. Where a dying HDD clicks, grinds and slows for weeks before giving up, an SSD usually behaves perfectly… until one morning it doesn't boot at all. These are the five subtle warning signs we look for at the Repair Point bench.",
      },
      { type: "h2", text: "1. Files take seconds to open that used to be instant" },
      {
        type: "p",
        text: "SSDs slow down dramatically when the controller starts retrying reads on weak NAND cells. If a 5 MB Word document suddenly takes 4 seconds to open — but only sometimes — your drive is asking for help.",
      },
      { type: "h2", text: "2. Random freezes or 'Not Responding' on save" },
      {
        type: "p",
        text: "Brief 5–30 second freezes during file saves are a classic late-stage SSD symptom. The drive is reallocating data away from failing sectors and the queue backs up while it does. Reboot 'fixes' it for a few hours — until it doesn't.",
      },
      { type: "h2", text: "3. Mysterious chkdsk or fsck errors" },
      {
        type: "p",
        text: "Windows running chkdsk on boot, or macOS launching Disk Utility's first-aid automatically, are both signs the file system is finding corruption. The file system is rarely the actual problem — the underlying storage is.",
      },
      { type: "h2", text: "4. Files vanish or refuse to open" },
      {
        type: "p",
        text: "Documents that won't open, photos that show a corrupted thumbnail, or folders that appear empty until you refresh — these are the most alarming signs. The data is still there, but the drive can no longer guarantee returning it.",
      },
      {
        type: "callout",
        title: "If you see this — stop and back up.",
        text: "Once an SSD starts losing files, the next failure usually takes the whole drive offline. Power off, clone the drive to a healthy one (we can do this same-day), then keep using the clone. Continuing to use a failing SSD risks total, permanent data loss.",
      },
      { type: "h2", text: "5. SMART warnings or critical sectors" },
      {
        type: "p",
        text: "Every SSD reports its health via SMART attributes. The most important ones are: Wear Leveling Count, Reallocated Sectors, Reported Uncorrectable Errors and Available Spare. If any of these are below the manufacturer threshold, the drive is on its last legs.",
      },
      { type: "h2", text: "Free tools to check yourself" },
      {
        type: "ul",
        items: [
          "Windows: CrystalDiskInfo (free, 60-second download). Look at the top-left status — 'Good' is fine, 'Caution' means replace soon, 'Bad' means replace today.",
          "macOS: open Disk Utility → First Aid. For deeper info, the smartmontools project (smartctl) reports SMART data on Macs too.",
          "Both: built-in chkdsk /r (Windows) and Disk Utility First Aid (Mac) confirm file-system damage, but neither tests the underlying flash health.",
        ],
      },
      { type: "h2", text: "When to call us" },
      {
        type: "p",
        text: "An SSD swap on a modern laptop is a 45-minute job: clone, swap, verify, re-install. A 1 TB NVMe drive is around $79 in 2026 and a fresh SSD will typically give a laptop 3–5 more years of useful life. Don't wait until it fails completely — recovering from a dead SSD costs ten times more than replacing a dying one.",
      },
      {
        type: "cta",
        text: "Worried about your drive? We'll run a full SMART diagnostic remotely for free and tell you honestly whether to clone now or wait. Book a $49 diagnostic — refunded if you proceed with the repair.",
      },
    ],
  },

  {
    slug: "best-free-software-every-mac-owner-should-have",
    title: "Best Free Software Every Mac Owner Should Have in 2026",
    description:
      "Twelve genuinely useful free apps that solve real macOS problems — picked by our technicians, with zero affiliate links and zero paid placements. Install once, thank us later.",
    cover:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A clean MacBook on a wooden desk next to a coffee mug",
    date: "2026-04-16",
    readTime: 7,
    tags: ["macOS", "Software", "Productivity"],
    relatedService: "mac-cleanup",
    blocks: [
      {
        type: "p",
        text: "macOS already ships with most of what most people need, but a few free third-party tools genuinely fix common annoyances. We install these on every Mac we service. No affiliate links, no sponsorship — just what our technicians actually use.",
      },
      { type: "h2", text: "Essential utilities" },
      {
        type: "ul",
        items: [
          "Rectangle — keyboard shortcut window snapping (left half, right half, full screen). The single biggest productivity win on a Mac.",
          "AppCleaner — drag any app onto it to fully uninstall, including hidden plist and cache files. Free, simple, no nags.",
          "The Unarchiver — opens .rar, .7z, .tar and every other format the built-in unzipper refuses.",
          "iStat Menus (free trial → $9.99) — best-in-class CPU, RAM, network and battery monitors in your menu bar.",
        ],
      },
      { type: "h2", text: "Browser and security" },
      {
        type: "ul",
        items: [
          "Brave or Firefox — both block trackers and ads at the browser level. Faster than Chrome on the same Mac.",
          "1Password (free 14-day) or Bitwarden (free forever) — password manager. Yes, you need one. No, the iCloud Keychain alone isn't quite enough.",
          "Little Snitch (free trial) or LuLu (free) — outbound firewall. Tells you when an app phones home so you can block sketchy ones.",
        ],
      },
      { type: "h2", text: "Productivity" },
      {
        type: "ul",
        items: [
          "Raycast — replaces Spotlight with a faster launcher, clipboard history, snippets and emoji picker. Free.",
          "Notion or Obsidian — note-taking. Notion is cloud-based, Obsidian is local-first; both have generous free tiers.",
          "Bartender 5 — hides menu bar icons you rarely use. Especially handy on the smaller MacBook Air screens.",
        ],
      },
      { type: "h2", text: "Media and screen tools" },
      {
        type: "ul",
        items: [
          "IINA — open-source media player that plays literally everything QuickTime can't.",
          "Kap — minimalist screen recorder that exports GIFs/MP4s directly. Free.",
          "Permute — file conversion (video, audio, image) without uploading anything to the cloud.",
        ],
      },
      {
        type: "callout",
        title: "Avoid these like the plague.",
        text: "Skip 'Mac cleaner' apps (MacKeeper, CleanMyMac aggressive variants), free 'driver updaters', and antivirus suites that nag constantly. macOS's built-in protection is sufficient for most users; bundled cleaners typically slow the system down further.",
      },
      { type: "h2", text: "Where to download safely" },
      {
        type: "p",
        text: "Always download from the developer's official website or the Mac App Store. Even seemingly reputable download portals (Softonic, MacUpdate) have, in the past, bundled adware. If we're sending you a recommendation, we'll always include the direct developer URL.",
      },
      { type: "h2", text: "One-time set-up service" },
      {
        type: "p",
        text: "If you'd rather not spend an evening installing and configuring twelve apps, we'll do a clean new-Mac setup remotely in about 90 minutes — including migration from your old Mac. Flat rate, with a written checklist of everything we installed and why.",
      },
      {
        type: "cta",
        text: "New Mac arriving soon? Book our remote Mac setup service and we'll deliver it pre-configured with everything from this list, ready to use.",
      },
    ],
  },

  {
    slug: "is-it-worth-repairing-my-old-laptop",
    title: "Is It Worth Repairing My Old Laptop? An Honest Decision Guide (2026)",
    description:
      "Torn between fixing a five-year-old laptop or buying new? Here is the exact rule-of-thumb our technicians use, plus five questions that decide it every time.",
    cover:
      "https://images.unsplash.com/photo-1531297484001-80022131f5a1?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "An open laptop on a desk, half-lit by afternoon sun",
    date: "2026-04-23",
    readTime: 6,
    tags: ["Buying guide", "Hardware"],
    relatedService: "hardware",
    blocks: [
      {
        type: "p",
        text: "We get asked this three or four times a week: 'My laptop is slow — is it worth fixing or should I just buy a new one?' It's an honest question, and the honest answer depends on five things. Here is the framework we use at Repair Point, with no upsell attached.",
      },
      { type: "h2", text: "The 50% rule" },
      {
        type: "p",
        text: "If the total repair cost is less than 50% of a comparable replacement, repair almost always wins. Above 50% and you should think hard. Above 75% and only a sentimental attachment justifies fixing.",
      },
      {
        type: "callout",
        title: "Worked example",
        text: "A 2020 MacBook Air with a bulging battery, sticky keyboard and 128 GB SSD would cost about $310 to make like new. A 2026 M4 MacBook Air starts around $1,099. Repair is 28% of replacement — go ahead, fix it.",
      },
      { type: "h2", text: "5 questions that decide it" },
      {
        type: "ol",
        items: [
          "How old is the laptop? Under 5 years — repair is usually worth it. Over 7 years — replacement almost always wins.",
          "Is the problem software or hardware? Software issues (viruses, OS reinstall) are cheap. Motherboard and screen replacements on old laptops are not.",
          "Will the manufacturer still update the OS? An out-of-support Windows 10 machine after October 2025 is a security liability, not a bargain.",
          "How much do you actually use it? A rarely-used household laptop can be kept alive cheaply. A daily driver deserves to be reliable.",
          "Are parts still available OEM? Once we can only source used parts, the workmanship warranty becomes 90 days instead of a year.",
        ],
      },
      { type: "h2", text: "The single upgrade that almost always works" },
      {
        type: "p",
        text: "For any laptop from 2016–2021 with an HDD or a small SSD: a clean SSD swap and clean OS install. Total cost $99–$149, turnaround under 2 hours, and the machine feels like a new $700 laptop for another 3 years. This is the highest-value tech spend we recommend, hands down.",
      },
      { type: "h2", text: "When to skip the repair" },
      {
        type: "ul",
        items: [
          "Cracked motherboard on an out-of-warranty ultrabook.",
          "Water damage requiring board-level rework on a 6-year-old machine.",
          "A laptop that already got a display replacement, then a battery, then a keyboard.",
          "Any 2015-or-older Intel MacBook with a battery under 60% capacity.",
        ],
      },
      { type: "h2", text: "Environmental angle" },
      {
        type: "p",
        text: "A laptop's manufacturing carbon footprint is about 60% of its lifetime emissions. Every extra year we keep a working laptop alive is a genuine sustainability win. If you can repair — do.",
      },
      {
        type: "cta",
        text: "Not sure which side of the line your laptop falls on? Send us its make, model and issue — we'll give you a free written honest recommendation, even if the answer is 'buy new'.",
      },
    ],
  },

  {
    slug: "windows-11-vs-windows-10-should-you-upgrade",
    title: "Windows 11 vs Windows 10 in 2026 — Should You Upgrade Now?",
    description:
      "Windows 10 support ended in October 2025. What that actually means for your PC, why waiting can be risky, and how to know if your hardware is eligible.",
    cover:
      "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A Windows laptop on a desk beside a coffee cup",
    date: "2026-04-30",
    readTime: 6,
    tags: ["Windows", "Buying guide", "Security"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "Microsoft ended free security updates for Windows 10 on 14 October 2025. In 2026, running Windows 10 without an Extended Security Updates (ESU) subscription means your PC is picking up unpatched vulnerabilities every month. Here is what to do.",
      },
      { type: "h2", text: "The short answer" },
      {
        type: "p",
        text: "If your PC is Windows-11 eligible — upgrade. It's free, the OS is stable, and the security gap will keep growing. If your PC isn't eligible, keep reading — there are three legitimate options.",
      },
      { type: "h2", text: "Is my PC eligible?" },
      {
        type: "ol",
        items: [
          "Open Start → type 'PC Health Check' and run the tool. Green tick = eligible.",
          "Or manually: 8th-gen Intel Core / 2nd-gen AMD Ryzen or newer, TPM 2.0, Secure Boot capable, 4 GB RAM, 64 GB storage.",
          "Most laptops sold after mid-2018 qualify. Most before then do not — even if the CPU feels fast enough.",
        ],
      },
      { type: "h2", text: "What actually changes with Windows 11" },
      {
        type: "ul",
        items: [
          "New Start menu (centred by default — you can move it left in Settings).",
          "Snap Layouts — hover the maximise button for pre-set window arrangements.",
          "DirectStorage and improved gaming performance on modern GPUs.",
          "Better power efficiency on laptops — 5–10% real-world battery gain.",
          "Copilot AI features (opt-in) integrated into the taskbar.",
        ],
      },
      {
        type: "callout",
        title: "Almost nothing breaks.",
        text: "In 200+ upgrades we've done this year, 96% of Windows 10 applications ran identically on Windows 11 after a clean upgrade. The exceptions were 32-bit legacy accounting apps and very old printer drivers.",
      },
      { type: "h2", text: "If your PC isn't eligible" },
      {
        type: "ol",
        items: [
          "Buy Extended Security Updates. Consumer ESU costs about $30/year for one PC — buys you time to plan a replacement.",
          "Switch the machine to a lightweight Linux distribution (Linux Mint, Zorin OS). Free, secure, and preserves the machine as a browser/email box.",
          "Replace it. If it's older than 6 years and daily-driven, replacement is usually the calmer answer.",
        ],
      },
      { type: "h2", text: "The upgrade itself" },
      {
        type: "p",
        text: "A clean Windows 11 upgrade with data migration and driver audit takes about 90 minutes at our workshop and is $79 flat-rate. We include a written pre-upgrade compatibility report so you know exactly which apps to test after.",
      },
      {
        type: "cta",
        text: "Book a $79 Windows 11 upgrade — includes compatibility check, clean install, driver refresh and post-upgrade tune-up. No-fix-no-fee if your PC isn't eligible.",
      },
    ],
  },

  {
    slug: "why-is-my-wifi-so-slow-at-home",
    title: "Why Is My Wi-Fi So Slow at Home? 8 Real Causes and Simple Fixes",
    description:
      "Buffering movies, laggy calls, dropped connections? Nine times out of ten it isn't your ISP. Here are the eight causes our technicians see in real houses — and the exact fix for each.",
    cover:
      "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A modern wireless router on a shelf with a soft blue LED",
    date: "2026-05-07",
    readTime: 7,
    tags: ["Networking", "Wi-Fi", "Troubleshooting"],
    relatedService: "remote-support",
    blocks: [
      {
        type: "p",
        text: "Slow home Wi-Fi is one of our most-booked remote support tickets — and the real culprit is almost never the internet plan. Here are the eight causes we solve every week, ranked by how often we find them.",
      },
      { type: "h2", text: "1. Router placed in the wrong spot" },
      {
        type: "p",
        text: "A router in a cupboard, behind a TV or in a corner will drop signal strength by up to 40%. Place it central, high up, and away from metal and microwaves. This single change fixes more slow-Wi-Fi tickets than any other.",
      },
      { type: "h2", text: "2. Old 2.4 GHz-only devices dragging the network down" },
      {
        type: "p",
        text: "If an old printer or smart plug only supports 2.4 GHz, it can slow down every device on that band. Move slow devices to a dedicated 2.4 GHz guest network and keep the main network on 5 GHz or Wi-Fi 6 6 GHz.",
      },
      { type: "h2", text: "3. Interference from neighbours' Wi-Fi" },
      {
        type: "p",
        text: "In apartments, dozens of overlapping networks can crush your throughput. Log into your router's admin page and manually set the 2.4 GHz channel to 1, 6 or 11 (whichever is least crowded). Use a free tool like WiFi Analyzer to check.",
      },
      { type: "h2", text: "4. Firmware not updated in 2+ years" },
      {
        type: "p",
        text: "Consumer routers ship with buggy firmware. Manufacturers push updates that fix speed and security — but almost no one applies them. Check your router's admin page (usually 192.168.0.1 or 192.168.1.1) for a Firmware Update section. This is a 5-minute job.",
      },
      { type: "h2", text: "5. Too many devices for the router's class" },
      {
        type: "p",
        text: "The £30 router your ISP shipped is rated for about 15 concurrent devices. A modern smart home easily has 30+ (phones, laptops, TVs, smart plugs, cameras, thermostats). An entry-level Wi-Fi 6 router with 5+ streams roughly doubles real-world throughput.",
      },
      {
        type: "callout",
        title: "Quick test",
        text: "Speedtest.net right next to your router, then again from the far room. If close-up is fast but far-away is slow — you have a coverage problem (add a mesh node). If both are slow — you have an internet or router problem.",
      },
      { type: "h2", text: "6. Bad Ethernet cabling to the router" },
      {
        type: "p",
        text: "A cracked or Cat-5 (not Cat-5e) cable between the ONT/modem and your router will cap your entire home at 100 Mbps regardless of the plan. Costs $5 to replace and often unlocks 3–5× the actual speed.",
      },
      { type: "h2", text: "7. One neighbour or roommate saturating the connection" },
      {
        type: "p",
        text: "A single device downloading a game or backing up to the cloud can saturate your upload bandwidth and make every video call laggy. QoS (Quality of Service) settings on modern routers let you prioritise video calls — we set this up in most remote sessions.",
      },
      { type: "h2", text: "8. ISP hardware genuinely failing" },
      {
        type: "p",
        text: "Rare, but real: a slowly dying ISP-supplied modem can lose 30–50% of throughput before it fully fails. If speeds are bad from a wired connection right next to the modem, call your ISP for a replacement — usually free.",
      },
      { type: "h2", text: "Mesh vs extender vs new router" },
      {
        type: "ul",
        items: [
          "Coverage problem in a large home → mesh Wi-Fi 6 system (Deco, eero, Nest).",
          "One dead room only → a good extender is fine.",
          "Old router (>5 years) → replace outright, don't add hardware to it.",
        ],
      },
      {
        type: "cta",
        text: "Book a $59 remote Wi-Fi tune-up — we log into your router, fix channels, firmware, QoS and device placement recommendations. Typical result: 2–4× real-world speed improvement.",
      },
    ],
  },

  {
    slug: "phishing-emails-spot-them-in-5-seconds",
    title: "Phishing Emails: How Our Technicians Spot Them in 5 Seconds",
    description:
      "The five tells our security team looks for on every suspicious email — plus the two rules that mean you will never fall for a phishing attack, even a really convincing one.",
    cover:
      "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A laptop showing an email inbox with a warning icon overlay",
    date: "2026-05-14",
    readTime: 6,
    tags: ["Security", "Phishing", "Email"],
    relatedService: "antivirus-setup",
    blocks: [
      {
        type: "p",
        text: "AI has made phishing emails better-looking than ever — perfect grammar, real logos, matching design. But the underlying tricks haven't changed. Here are the five checks we run in about five seconds on any suspicious message, and the two rules that make it almost impossible to fall for one.",
      },
      { type: "h2", text: "The 5-second checklist" },
      {
        type: "ol",
        items: [
          "Hover over the sender's name — does the actual email address match the brand? 'Apple Support <noreply@apple-sec-team.co>' is not Apple.",
          "Hover (don't click) every link. Read the true destination in the status bar. A link that says 'chase.com' but goes to 'chase-secure-login-verify.xyz' is phishing.",
          "Look for urgency + fear ('Account locked in 24 hours!'). Legitimate brands almost never rely on countdown-timer pressure.",
          "Check whether they know your details. A real bank uses your name; a phishing 'bank' says 'Dear Customer'.",
          "Ask yourself: does this action make sense? Real Amazon never asks you to log in via an email to check on a delivery.",
        ],
      },
      { type: "h2", text: "The 2025 twist: AI-personalised phishing" },
      {
        type: "p",
        text: "In 2025 we started seeing phishing emails that quote your real LinkedIn job title, mention your actual boss's name and reference a real project. Attackers scrape public data and use AI to personalise. Rule of thumb: if the message is unexpected and asks you to click or pay, verify by a different channel — phone, in-person or a fresh browser tab.",
      },
      {
        type: "callout",
        title: "The two rules that keep you safe",
        text: "Rule 1 — Never enter a password from a link in an email. Always type the site's URL yourself or use a bookmarked link. Rule 2 — Never approve an MFA prompt you didn't just request. If your phone buzzes for a login you didn't start, someone else has your password. Change it immediately.",
      },
      { type: "h2", text: "Common 2026 phishing themes" },
      {
        type: "ul",
        items: [
          "Fake courier delivery updates (USPS, FedEx, DHL) asking for a small handling fee.",
          "'Your Apple ID has been used to sign in on a new device' with a suspicious verify link.",
          "Fake invoices from suppliers you actually use — a favourite for small businesses.",
          "'Payroll update — verify your bank details' emails targeting HR staff.",
          "Fake Microsoft 365 login screens with your real company logo scraped from LinkedIn.",
        ],
      },
      { type: "h2", text: "What to do if you clicked one" },
      {
        type: "ol",
        items: [
          "If you entered a password anywhere — change it immediately on the real site, from a different device.",
          "Enable MFA on the affected account, then revoke all active sessions.",
          "Check the account's recent sign-in activity and any auto-forwarding email rules (attackers often set these to hide traces).",
          "Scan the device with Windows Defender Offline or a reputable Mac scanner in case malware was dropped.",
          "If it's a work account — tell IT within an hour. The clock matters.",
        ],
      },
      { type: "h2", text: "For small businesses" },
      {
        type: "p",
        text: "One employee clicking one phishing link is still the #1 way small businesses get breached. A 30-minute team training + Microsoft 365 or Google Workspace hardening reduces the risk by around 90%. We include this in every Fleet Maintenance Plan.",
      },
      {
        type: "cta",
        text: "Suspicious email in your inbox right now? Forward it to us and we'll verify it within one business hour — free, no obligation.",
      },
    ],
  },

  {
    slug: "why-does-my-mac-get-so-hot",
    title: "Why Does My Mac Get So Hot? The 4 Real Causes (and Fixes)",
    description:
      "Fans screaming, chassis too hot to touch? Here are the four genuine causes of an overheating Mac our technicians see most — and what actually cools it down.",
    cover:
      "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A MacBook on a desk with the fan visibly running",
    date: "2026-05-21",
    readTime: 6,
    tags: ["macOS", "Performance", "Hardware"],
    relatedService: "mac-cleanup",
    blocks: [
      {
        type: "p",
        text: "A hot Mac isn't just uncomfortable — sustained heat throttles performance and shortens the life of the battery and logic board. Here are the four real causes, ranked by how often we see them at the workshop.",
      },
      { type: "h2", text: "1. A runaway background process" },
      {
        type: "p",
        text: "By far the most common cause. Open Activity Monitor (Spotlight → 'Activity Monitor') and sort by % CPU. Culprits we see weekly: 'mediaanalysisd' (Photos scanning your library), a stuck browser tab, Spotlight re-indexing, or a crashed helper looping at 100%. Quit the offender and the fans stop within a minute.",
      },
      { type: "h2", text: "2. Too many browser tabs (especially Chrome)" },
      {
        type: "p",
        text: "Each heavy tab — Google Docs, Figma, YouTube, Slack — is a small computer. Thirty open tabs will keep any Mac's fans running. Use a tab suspender, switch to Safari (far more efficient on Apple Silicon), and close what you're not using.",
      },
      { type: "h2", text: "3. Dust-clogged vents (Intel Macs & older MacBook Pros)" },
      {
        type: "p",
        text: "Three-plus years of dust builds an insulating blanket over the heatsink. The fan spins faster and faster but can't move enough air. A professional internal clean and fresh thermal paste drops idle temps 10–20 °C and is a $59 flat-rate remote-guided or workshop service.",
      },
      {
        type: "callout",
        title: "Apple Silicon runs cool — mostly.",
        text: "M1/M2/M3/M4 Macs rarely overheat from hardware. If your Apple Silicon Mac is hot, it's almost always software (a runaway process) — not dust. Check Activity Monitor first.",
      },
      { type: "h2", text: "4. Blocked airflow + hot environment" },
      {
        type: "ul",
        items: [
          "Using the Mac on a bed, sofa or lap blocks the bottom/rear vents. Use a hard flat surface or a $15 stand.",
          "Direct sunlight or a hot car pushes ambient temperature past the safe range.",
          "A cheap third-party charger can cause the Mac to run warm during charge — use genuine or reputable USB-C PD chargers.",
        ],
      },
      { type: "h2", text: "Tools to monitor temperature" },
      {
        type: "p",
        text: "Install a free menu-bar monitor like Stats (open-source) or the trial of iStat Menus to watch CPU temperature and fan RPM in real time. If idle temps sit above 60 °C with nothing running, something is wrong.",
      },
      {
        type: "cta",
        text: "Mac still overheating after the basics? Book a $59 remote diagnostic — we'll find the runaway process or confirm it needs a physical clean, and tell you honestly which.",
      },
    ],
  },

  {
    slug: "how-to-securely-wipe-an-old-pc-before-selling",
    title: "How to Securely Wipe an Old PC or Mac Before Selling It",
    description:
      "Factory reset is NOT enough. Here is how to properly erase a Windows PC or Mac so your files, passwords and accounts can never be recovered by the next owner.",
    cover:
      "https://images.unsplash.com/photo-1483478550801-ceba5fe50e8e?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "An old laptop being prepared for resale on a desk",
    date: "2026-05-28",
    readTime: 6,
    tags: ["Security", "Privacy", "Windows", "macOS"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "Selling, donating or recycling an old computer? A simple 'delete files' or even a quick factory reset can leave your tax returns, saved passwords and logged-in accounts recoverable with free tools. Here's how to do it properly.",
      },
      { type: "h2", text: "Before you wipe: 4 essential steps" },
      {
        type: "ol",
        items: [
          "Back up everything you want to keep to an external drive or the cloud.",
          "Sign out of and de-authorise accounts: iCloud, iTunes, Adobe, Microsoft 365, Dropbox.",
          "Turn off Find My Mac (macOS) or your Microsoft account device link (Windows) so the new owner isn't locked out.",
          "Deactivate any software licences tied to the hardware (Office, Photoshop, antivirus).",
        ],
      },
      { type: "h2", text: "Windows: the proper wipe" },
      {
        type: "ol",
        items: [
          "Settings → System → Recovery → Reset this PC → Remove everything.",
          "Choose 'Local reinstall' or 'Cloud download'.",
          "Critical: click 'Change settings' and turn ON 'Clean data' — this overwrites the drive so files can't be recovered. (This step is the one most people miss.)",
          "Let it run — it can take 1–3 hours with clean-data enabled. That's normal.",
        ],
      },
      { type: "h2", text: "Mac: the proper wipe" },
      {
        type: "ol",
        items: [
          "Apple Silicon or T2 Macs: simply use System Settings → General → Transfer or Reset → Erase All Content and Settings. Because the SSD is hardware-encrypted, this instantly and irrecoverably wipes it.",
          "Older Intel Macs without T2: boot to Recovery (Cmd+R), open Disk Utility, erase the drive as APFS, then reinstall macOS.",
        ],
      },
      {
        type: "callout",
        title: "Modern SSDs are encrypted — use it.",
        text: "On any Mac with Apple Silicon or a T2 chip, and any Windows PC with BitLocker on, erasing the encryption key instantly makes all data unrecoverable. This is faster and more secure than old 'multi-pass overwrite' methods, which can actually shorten SSD life.",
      },
      { type: "h2", text: "If the PC won't boot or you're recycling it" },
      {
        type: "p",
        text: "If the machine is dead and headed for recycling, the only guaranteed method is to physically remove the drive and either keep it, or (for HDDs) destroy the platters. For SSDs, a secure-erase utility from the manufacturer is best. We handle certified data destruction for businesses on request.",
      },
      {
        type: "cta",
        text: "Selling a fleet of old business machines? We provide certified secure-wipe with a data-destruction certificate for compliance. Get in touch for a quote.",
      },
    ],
  },

  {
    slug: "the-truth-about-pc-optimisers",
    title: "The Truth About 'PC Optimisers' (and What Actually Speeds Up a PC)",
    description:
      "Those 'speed up your PC' apps advertised everywhere? Here's what they really do, why they often make things worse, and the free steps that genuinely work.",
    cover:
      "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "Lines of code on a dark screen representing system software",
    date: "2026-06-04",
    readTime: 6,
    tags: ["Windows", "Performance", "Security"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "You've seen the ads: 'Your PC is slow — download SpeedBooster Pro to fix 3,472 errors!' We remove these apps from customers' machines every week. Here's the honest truth about what they do.",
      },
      { type: "h2", text: "What 'optimisers' actually do" },
      {
        type: "ul",
        items: [
          "Registry cleaners: fix problems that don't cause slowdowns. Windows doesn't slow down from 'registry junk' — that's a 2005 myth.",
          "'Error scanners': invent scary numbers ('3,472 issues!') to pressure you into paying for the full version.",
          "Driver updaters: often install the wrong drivers and break things that worked fine.",
          "Many run constantly in the background — themselves becoming a source of slowdown.",
        ],
      },
      {
        type: "callout",
        title: "Some are outright malware.",
        text: "A meaningful share of 'free PC optimisers' bundle adware, browser hijackers or spyware. If one is already installed, uninstall it — and if you paid, dispute the charge with your card provider.",
      },
      { type: "h2", text: "What actually speeds up a Windows PC (all free)" },
      {
        type: "ol",
        items: [
          "Add an SSD if you still have a spinning hard drive. Single biggest real-world speed-up, every time.",
          "Free up space — keep at least 15% of the drive empty.",
          "Trim startup apps: Ctrl+Shift+Esc → Startup tab → disable high-impact items you don't need.",
          "Install pending Windows updates and reboot fully.",
          "Run the built-in tools: 'Disk Cleanup', and in an admin terminal, sfc /scannow.",
          "Add RAM if you routinely use more than 80% (check Task Manager → Performance).",
        ],
      },
      { type: "h2", text: "For Macs" },
      {
        type: "p",
        text: "The same applies — skip 'Mac cleaner' apps entirely. Use built-in Storage management, quit runaway processes in Activity Monitor, and keep macOS updated. That's genuinely all most Macs need.",
      },
      { type: "h2", text: "The one 'optimiser' worth paying for: a human" },
      {
        type: "p",
        text: "A proper professional tune-up does what no app can: it diagnoses the actual bottleneck (disk, RAM, heat, malware, or a failing component) and fixes that specific cause. That's why our $79 flat-rate tune-up reliably works where a $40 'optimiser subscription' doesn't.",
      },
      {
        type: "cta",
        text: "Got a 'PC optimiser' installed and a slow machine? Book a $79 tune-up — we'll remove the bloatware and fix the real cause, in writing, first.",
      },
    ],
  },

  {
    slug: "macbook-liquid-damage-first-60-minutes",
    title: "MacBook Liquid Damage: Exactly What to Do in the First 60 Minutes",
    description:
      "Spilled coffee, water or wine on your MacBook? The first hour decides whether it survives. Here is the calm, step-by-step checklist our technicians follow.",
    cover:
      "https://images.unsplash.com/photo-1588600878108-578307a3cc9d?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A coffee cup beside a laptop on a wooden desk",
    date: "2026-06-11",
    readTime: 5,
    tags: ["macOS", "Hardware", "Emergency"],
    relatedService: "hardware",
    blocks: [
      {
        type: "p",
        text: "Liquid on a MacBook is a race against corrosion, not just short-circuits. What you do in the first 60 minutes matters more than anything a repair shop can do afterwards. Follow these steps in order — and skip the rice.",
      },
      { type: "h2", text: "Minute 0–2: Power off immediately" },
      {
        type: "p",
        text: "Hold the power button for 10 seconds to force a shutdown. Do NOT wait to save your work. Electricity + liquid = corrosion and short circuits. Every second powered-on increases the damage.",
      },
      { type: "h2", text: "Minute 2–5: Unplug everything" },
      {
        type: "p",
        text: "Disconnect the charger and any USB/accessories. If you're comfortable and it's a spill you can't stop, disconnecting the battery is ideal — but only if you have the tools and confidence. Otherwise leave it to a technician.",
      },
      { type: "h2", text: "Minute 5–10: Drain, don't spread" },
      {
        type: "ol",
        items: [
          "Open the lid fully and turn the MacBook upside-down into a tent/A-shape.",
          "This lets liquid drain OUT rather than deeper into the logic board.",
          "Gently blot external liquid with a lint-free cloth — don't push it around.",
        ],
      },
      {
        type: "callout",
        title: "Do NOT use rice. Do NOT use a hairdryer.",
        text: "Rice does almost nothing for internal moisture and leaves starch/dust behind. A hairdryer pushes liquid deeper and can melt components. Both are myths that make things worse.",
      },
      { type: "h2", text: "Minute 10–60: Get it to a technician" },
      {
        type: "p",
        text: "The real fix is a professional teardown, ultrasonic cleaning of the logic board, and corrosion removal — ideally within 24–48 hours before corrosion sets in. Sugary or salty liquids (coffee, wine, soda) are far more corrosive than water, so speed matters even more.",
      },
      { type: "h2", text: "What NOT to do" },
      {
        type: "ul",
        items: [
          "Don't try to turn it on 'just to check' — this is how survivable spills become dead logic boards.",
          "Don't charge it.",
          "Don't assume it's fine because it still works — corrosion damage appears days or weeks later.",
        ],
      },
      {
        type: "cta",
        text: "Spilled something on your MacBook? Power it off now and contact us immediately — the sooner we clean the board, the higher the survival rate. We handle liquid-damage recovery with a no-fix-no-fee assessment.",
      },
    ],
  },

  {
    slug: "how-to-speed-up-chrome-on-a-slow-pc",
    title: "How to Speed Up Google Chrome on a Slow PC (2026)",
    description:
      "Chrome eating all your RAM and crawling to a halt? Here are the nine settings and habits our technicians use to make Chrome fast again — without switching browsers.",
    cover:
      "https://images.unsplash.com/photo-1600267175161-cfaa711b4a81?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A laptop screen showing a web browser on a desk",
    date: "2026-06-18",
    readTime: 6,
    tags: ["Windows", "Performance", "Browsers"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "Chrome is the world's most popular browser and also its most memory-hungry. If yours has become sluggish, you rarely need a new PC — you need these nine fixes. They take about ten minutes total.",
      },
      { type: "h2", text: "1. Close and audit extensions" },
      {
        type: "p",
        text: "Extensions are the #1 cause of a slow Chrome. Go to chrome://extensions and disable everything you don't use daily. Ad blockers, coupon finders and 'shopping assistants' are the worst offenders — some inject scripts into every page you load.",
      },
      { type: "h2", text: "2. Use the built-in Memory Saver" },
      {
        type: "p",
        text: "Settings → Performance → turn on Memory Saver. Chrome will freeze inactive tabs and free their RAM, reloading them instantly when you click back. On a 8 GB PC this alone transforms responsiveness.",
      },
      { type: "h2", text: "3. Kill runaway tabs with Chrome's Task Manager" },
      {
        type: "p",
        text: "Press Shift+Esc inside Chrome to open its own Task Manager. Sort by Memory — you'll often find one tab (a stuck web app, an autoplaying video) hogging 2 GB. End it.",
      },
      { type: "h2", text: "4. Clear cache and cookies" },
      {
        type: "p",
        text: "Settings → Privacy → Clear browsing data → cached images and files. A bloated cache slows page loads. Keep your passwords and history if you want — just clear the cache.",
      },
      {
        type: "callout",
        title: "The real bottleneck is often RAM.",
        text: "Chrome is designed to use spare RAM as a speed cache. On a 4 GB PC it will always struggle. Upgrading to 8 GB (or 16 GB) is the single most effective fix on older laptops — often under $40 in parts.",
      },
      { type: "h2", text: "5–9. Quick wins" },
      {
        type: "ol",
        items: [
          "Update Chrome — Settings → About Chrome. Outdated versions are slower and less secure.",
          "Turn off hardware acceleration if pages flicker (Settings → System), or turn it ON if your GPU is capable and it's off.",
          "Remove unused profiles — each open profile consumes memory.",
          "Reset Chrome to defaults if it's badly broken: Settings → Reset settings. Your bookmarks and passwords survive.",
          "As a last resort, uninstall and reinstall Chrome to clear a corrupted install.",
        ],
      },
      { type: "h2", text: "Still slow after all that?" },
      {
        type: "p",
        text: "If Chrome is still crawling, the problem is usually the PC itself — a full SSD, low RAM, malware or heat throttling. Our $79 tune-up diagnoses the actual cause and fixes it, browser and all.",
      },
      {
        type: "cta",
        text: "Chrome still sluggish? Book a $79 remote tune-up — we'll clean it up, check your RAM and disk, and tell you honestly if a small hardware upgrade would help.",
      },
    ],
  },

  {
    slug: "two-factor-authentication-10-minute-setup",
    title: "Two-Factor Authentication: The 10-Minute Setup That Stops 99% of Hacks",
    description:
      "Passwords get stolen every day — 2FA is what keeps the attacker out anyway. Here's what it is, why it matters, and how to set it up on your most important accounts in ten minutes.",
    cover:
      "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A smartphone showing a security authentication code",
    date: "2026-06-25",
    readTime: 6,
    tags: ["Security", "2FA", "Passwords"],
    relatedService: "antivirus-setup",
    blocks: [
      {
        type: "p",
        text: "Google reports that simply turning on two-factor authentication (2FA) blocks over 99% of automated account-takeover attacks. It's the single highest-impact security step you can take, and it takes about ten minutes. Here's how.",
      },
      { type: "h2", text: "What 2FA actually is" },
      {
        type: "p",
        text: "2FA means logging in needs two things: something you know (your password) and something you have (your phone). Even if a hacker steals your password in a data breach, they can't get in without your second factor.",
      },
      { type: "h2", text: "The three types, ranked" },
      {
        type: "ol",
        items: [
          "Best — Hardware key (YubiKey) or passkeys: phishing-proof, but overkill for most home users.",
          "Great — Authenticator app (Google Authenticator, Microsoft Authenticator, Authy): generates a 6-digit code that changes every 30 seconds. Recommended for everyone.",
          "Okay — SMS text codes: better than nothing, but vulnerable to SIM-swap attacks. Use only if the app option isn't available.",
        ],
      },
      { type: "h2", text: "Set it up on these accounts first (in order)" },
      {
        type: "ol",
        items: [
          "Your primary email (Gmail/Outlook) — this is the master key to everything else. Do this one first, today.",
          "Your password manager.",
          "Your bank and any financial apps.",
          "Apple ID / Google Account (controls your phone and backups).",
          "Social media and anything with your card saved (Amazon, PayPal).",
        ],
      },
      {
        type: "callout",
        title: "Save your backup codes.",
        text: "When you enable 2FA, each service gives you one-time backup codes. Print them or store them in your password manager. They're how you get back in if you lose your phone — don't skip this step.",
      },
      { type: "h2", text: "The 10-minute setup (authenticator app)" },
      {
        type: "ol",
        items: [
          "Install Google Authenticator or Microsoft Authenticator from your app store.",
          "In each account's Security settings, choose 'Two-factor / Two-step verification' → 'Authenticator app'.",
          "Scan the QR code with the app.",
          "Enter the 6-digit code it shows to confirm.",
          "Save the backup codes it gives you. Done.",
        ],
      },
      { type: "h2", text: "One golden rule" },
      {
        type: "p",
        text: "Never approve a 2FA prompt or read a code to anyone you didn't personally contact. If your phone buzzes with a login request you didn't start, someone has your password — deny it and change that password immediately.",
      },
      {
        type: "cta",
        text: "Want a hand locking down your accounts? Our remote security session sets up 2FA across your key accounts, cleans up old logins and checks for breaches — flat rate, done in one sitting.",
      },
    ],
  },

  {
    slug: "windows-wont-boot-7-recovery-steps",
    title: "Windows Won't Boot? 7 Recovery Steps to Try Before Reinstalling",
    description:
      "That black screen or 'preparing automatic repair' loop doesn't always mean the end. Here are the seven recovery steps our technicians run — in order — before ever considering a wipe and reinstall.",
    cover:
      "https://images.unsplash.com/photo-1587202372634-32705e3bf49c?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A laptop showing a Windows recovery screen",
    date: "2026-07-02",
    readTime: 7,
    tags: ["Windows", "Troubleshooting", "Recovery"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "'Preparing Automatic Repair' spinning forever. A black screen with only a cursor. A blue screen the moment Windows starts. It's not always dead — most boot problems are software and recoverable. Here's the exact order we work through at the workshop.",
      },
      { type: "h2", text: "Step 1: Try a proper power cycle" },
      {
        type: "p",
        text: "Hold the power button for 30 seconds with the charger removed. Reconnect power and boot. This clears residual state and resolves 20% of boot loops on its own.",
      },
      { type: "h2", text: "Step 2: Boot into Safe Mode" },
      {
        type: "p",
        text: "Force three failed boots (power on, hold power to shut down as the logo appears). On the fourth attempt Windows enters the Recovery Environment. Choose Advanced options → Startup Settings → Restart → press 4 for Safe Mode. If Safe Mode works, the culprit is a driver or startup app.",
      },
      { type: "h2", text: "Step 3: Uninstall the last update or driver" },
      {
        type: "p",
        text: "In Safe Mode, open Settings → Windows Update → Update history → Uninstall updates. Remove the most recent one and reboot. If the problem appeared right after a Windows update, this fixes it 80% of the time.",
      },
      { type: "h2", text: "Step 4: Startup Repair from Recovery" },
      {
        type: "p",
        text: "From the Recovery Environment: Troubleshoot → Advanced options → Startup Repair. Let it run — it can take 20 minutes. It fixes broken boot configuration data (BCD) without touching your files.",
      },
      { type: "h2", text: "Step 5: System Restore" },
      {
        type: "p",
        text: "Advanced options → System Restore → pick a restore point from before the trouble started. Files are preserved, but recent app installs and driver changes are rolled back.",
      },
      {
        type: "callout",
        title: "Back up your files before Step 6.",
        text: "If none of the above worked, connect a USB drive and copy your critical files out via the Recovery Environment's Command Prompt or a Linux live USB before trying repairs that touch the disk.",
      },
      { type: "h2", text: "Step 6: Repair the system files" },
      {
        type: "ol",
        items: [
          "Boot to Recovery → Command Prompt.",
          "Run: sfc /scannow /offbootdir=C:\\ /offwindir=C:\\Windows",
          "Then: DISM /Image:C:\\ /Cleanup-Image /RestoreHealth /Source:WIM:D:\\sources\\install.wim:1 (adjust drive letters).",
          "Reboot.",
        ],
      },
      { type: "h2", text: "Step 7: Rebuild the boot record" },
      {
        type: "p",
        text: "From Command Prompt in Recovery: bootrec /fixmbr, then bootrec /fixboot, then bootrec /rebuildbcd. This fixes corrupted boot sectors caused by failed updates or dual-boot changes.",
      },
      { type: "h2", text: "If none of that works" },
      {
        type: "p",
        text: "You're likely looking at a failing SSD, a bad RAM stick, or corruption too deep for in-place repair. Our $49 diagnostic tells you within an hour whether it's a hardware fault or a full reinstall — and we recover data from failing drives first, always.",
      },
      {
        type: "cta",
        text: "Windows still refusing to boot? Book a $49 written diagnostic — we recover your data first, then tell you exactly what it'll take to bring the machine back.",
      },
    ],
  },

  {
    slug: "how-often-should-you-restart-your-mac",
    title: "How Often Should You Restart Your Mac? (And Why It Matters)",
    description:
      "Closing the lid isn't restarting. Here's how often you should actually reboot your Mac in 2026, what changes when you do, and the free half-minute habit that fixes half our incoming tickets.",
    cover:
      "https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A MacBook closed on a wooden desk beside a plant",
    date: "2026-07-09",
    readTime: 5,
    tags: ["macOS", "Performance", "Maintenance"],
    relatedService: "mac-cleanup",
    blocks: [
      {
        type: "p",
        text: "Half the 'my Mac is slow' tickets we get resolve with a single proper restart. But most Mac users go weeks or months between reboots — because closing the lid feels the same. Here's the honest guidance from our workshop.",
      },
      { type: "h2", text: "How often is 'right'?" },
      {
        type: "ul",
        items: [
          "Casual users (email, browsing, streaming): restart once a week.",
          "Power users (many apps, dev tools, video editing): restart at least twice a week.",
          "Everyone: restart after any macOS update and after installing any new app that added a system extension or helper.",
        ],
      },
      { type: "h2", text: "What a restart actually does" },
      {
        type: "ol",
        items: [
          "Clears RAM and dumps memory leaks from long-running apps.",
          "Resets kernel caches and system daemons that misbehave over time.",
          "Applies queued system updates and security patches.",
          "Rebuilds the launch services database (fixes 'default app to open this file' weirdness).",
          "Refreshes network stack — often fixes flaky Wi-Fi or VPN issues instantly.",
        ],
      },
      { type: "h2", text: "Sleep is not restart" },
      {
        type: "p",
        text: "When you close the lid, macOS pauses everything but keeps state. Every app, extension and cache remains loaded in RAM. Over weeks this accumulates — apps get sluggish, Spotlight lags, printers vanish, Wi-Fi drops. Only a real restart clears this.",
      },
      {
        type: "callout",
        title: "The 30-second habit that saves you an hour.",
        text: "Every Sunday evening: Apple menu → Restart. That's it. It takes 30 seconds and prevents the slow drift that leads to a bad Monday morning.",
      },
      { type: "h2", text: "What about shutdown vs restart?" },
      {
        type: "p",
        text: "Shutdown + power-on is functionally the same as restart on modern Macs — either is fine. What matters is that the kernel and RAM fully reset. Choose whichever fits your workflow (shutdown if you're storing the laptop; restart if you want to jump right back in).",
      },
      { type: "h2", text: "Signs you should restart NOW" },
      {
        type: "ul",
        items: [
          "Beach ball appearing more than twice a day.",
          "Apps refusing to open or update.",
          "Wi-Fi connected but no internet.",
          "External displays not recognised.",
          "General 'something feels off' vibe — usually right.",
        ],
      },
      {
        type: "cta",
        text: "Restarted your Mac and it's still slow? Book a $89 remote macOS tune-up — we go deeper than a reboot: caches, agents, storage, updates and hidden background processes, all cleaned in under an hour.",
      },
    ],
  },

  {
    slug: "cybersecurity-for-small-businesses-1-hour-checklist",
    title: "Cybersecurity for Small Businesses: A 1-Hour Checklist for 2026",
    description:
      "You don't need a CISO or a five-figure budget. Here's the exact 60-minute checklist we run for two-to-fifty-person businesses to stop 95% of real-world attacks.",
    cover:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A small team collaborating in a bright office",
    date: "2026-07-16",
    readTime: 7,
    tags: ["Security", "Small business", "Compliance"],
    relatedService: "antivirus-setup",
    blocks: [
      {
        type: "p",
        text: "Small businesses are now the #1 target for ransomware because they're big enough to pay and small enough not to have a security team. The good news: 95% of real-world attacks are stopped by the basics. Here is our full one-hour checklist — no jargon, no fear-selling.",
      },
      { type: "h2", text: "Part 1: Accounts (15 minutes)" },
      {
        type: "ol",
        items: [
          "Turn on 2FA for every email, cloud and admin account. This alone blocks 99% of automated attacks.",
          "Remove ex-employees and inactive accounts from every SaaS you pay for. Audit quarterly.",
          "Give every person their own login. Never share credentials — you can't audit or revoke a shared account.",
          "Use a company password manager (Bitwarden, 1Password Business, or Dashlane).",
        ],
      },
      { type: "h2", text: "Part 2: Email (15 minutes)" },
      {
        type: "ol",
        items: [
          "Enable Advanced Threat Protection in Microsoft 365 (Defender) or Google Workspace (Advanced Protection).",
          "Set SPF, DKIM and DMARC on your domain — stops attackers spoofing your business name.",
          "Turn on a strict 'external sender' warning banner. Cheapest, best anti-phishing training there is.",
          "Set up mail archiving / journaling if you're in a regulated industry.",
        ],
      },
      { type: "h2", text: "Part 3: Devices (15 minutes)" },
      {
        type: "ol",
        items: [
          "Enable full-disk encryption everywhere: BitLocker (Windows) or FileVault (Mac). A stolen laptop with encryption on is a nuisance; without, it's a breach.",
          "Enforce screen lock after 5 minutes.",
          "Turn on automatic OS and browser updates.",
          "Run one real-time antivirus per device (Windows Defender is fine).",
        ],
      },
      {
        type: "callout",
        title: "One lost laptop = a data-breach notification.",
        text: "Under GDPR, HIPAA and most US state laws, an unencrypted lost laptop is a reportable data breach. An encrypted one is not. This single toggle is worth thousands in fines and legal fees.",
      },
      { type: "h2", text: "Part 4: Backups (10 minutes)" },
      {
        type: "ul",
        items: [
          "Cloud-first: OneDrive/Google Drive for daily files, IDrive/Backblaze Business for machine images.",
          "Immutable snapshots — pick a provider that keeps 30 days of versions you can restore from.",
          "Test one restore per quarter. A backup you've never restored is a hope, not a backup.",
        ],
      },
      { type: "h2", text: "Part 5: People (5 minutes)" },
      {
        type: "p",
        text: "One 15-minute phishing awareness session a year cuts click-rate by around 70%. We include one with every Fleet Maintenance Plan. Also: publish a simple 'if in doubt, forward it to IT' rule so your team never feels stupid asking.",
      },
      { type: "h2", text: "For regulated industries" },
      {
        type: "p",
        text: "If you handle payment cards (PCI), health data (HIPAA), or EU citizen data (GDPR), the above is the floor, not the ceiling. Add endpoint detection (Defender for Business, SentinelOne), a written incident-response plan and an annual penetration test.",
      },
      {
        type: "cta",
        text: "Want us to run this checklist for you? Our $299 remote SMB security audit covers all of the above with a written report and prioritised fix list. Optional monthly Fleet Maintenance keeps everything current.",
      },
    ],
  },

  {
    slug: "how-to-migrate-from-a-dying-pc-to-a-new-one-safely",
    title: "How to Migrate from a Dying PC to a New One — Safely",
    description:
      "Your old PC is on its last legs and the new one just arrived. Here is exactly how to move everything across without losing files, passwords or your sanity — in the right order.",
    cover:
      "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "Two laptops open side by side on a desk during a migration",
    date: "2026-07-23",
    readTime: 6,
    tags: ["Windows", "macOS", "Migration"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "'Migrate everything from my old laptop' is one of our most-booked services. Done wrong, it's a mess of missing photos, broken email and locked software. Done in the right order, it's straightforward. Here's the exact sequence.",
      },
      { type: "h2", text: "1. Back up the old PC first" },
      {
        type: "p",
        text: "Before touching anything, make a complete backup: cloud sync (OneDrive/iCloud/Google Drive) plus a full image to an external drive using Macrium Reflect (Windows) or Time Machine (Mac). If anything goes wrong during migration, you can restore from this safely.",
      },
      { type: "h2", text: "2. Inventory what you actually need to move" },
      {
        type: "ul",
        items: [
          "Documents, photos, downloads — obvious files.",
          "Browser bookmarks, saved passwords and extensions — sync via Chrome/Firefox/Edge account, or your password manager.",
          "Email — for POP3 accounts, export or set them up as IMAP before migrating.",
          "Software licences — locate the keys and deactivate them on the old device.",
          "Local databases: Outlook .pst, QuickBooks files, iMessage/iPhoto libraries.",
        ],
      },
      { type: "h2", text: "3. Use the right migration tool" },
      {
        type: "ol",
        items: [
          "Windows → Windows: sign in to the new PC with the same Microsoft account and let OneDrive / Windows Backup sync your files. For local apps, reinstall from scratch (avoid old-PC 'clone' tools — they carry over bloat).",
          "Mac → Mac: Migration Assistant over Wi-Fi or a Thunderbolt cable. Choose selectively — don't drag everything; pick users, apps and files. Takes 30 mins to a few hours depending on size.",
          "Windows → Mac (or vice versa): manual transfer via cloud + reinstall each app on the new OS.",
        ],
      },
      {
        type: "callout",
        title: "Don't image-clone a failing drive to a healthy one.",
        text: "If your old PC is dying, cloning the disk carries over corruption and bad configurations. Better to selectively copy your data (documents, photos, exported settings) and reinstall apps fresh on the new device.",
      },
      { type: "h2", text: "4. Sign out & deactivate the old device" },
      {
        type: "ol",
        items: [
          "Sign out of iCloud, Microsoft account, iTunes and Adobe on the old machine.",
          "Deactivate any locked software licences (Photoshop, Office, antivirus, Ableton — most have a per-device seat limit).",
          "Turn off Find My Mac / device-lock links so the recycler or new owner isn't locked out.",
        ],
      },
      { type: "h2", text: "5. Verify on the new PC" },
      {
        type: "p",
        text: "Before you wipe the old one, verify on the new PC: open your key documents, send/receive a test email from each account, log into every service you use daily. Only when you've genuinely used the new machine for a full week should you wipe or sell the old one.",
      },
      { type: "h2", text: "6. Wipe the old device securely" },
      {
        type: "p",
        text: "Once you're confident everything is on the new machine, follow our secure-wipe guide before selling, donating or recycling the old one. Simply deleting files is never enough.",
      },
      {
        type: "cta",
        text: "New PC or Mac arriving soon? Book our $129 remote migration service — we back up, transfer, verify and set up the new device end-to-end. Zero downtime, guaranteed nothing lost.",
      },
    ],
  },

  {
    slug: "why-your-mac-says-disk-is-almost-full",
    title: "Why Your Mac Says 'Your Disk Is Almost Full' (Even After Cleaning)",
    description:
      "You've emptied Trash, cleared Downloads and the warning still pops up. Here's what's really eating your Mac's disk in 2026 — and how to reclaim tens of gigabytes in ten minutes.",
    cover:
      "https://images.unsplash.com/photo-1611746872915-64382b5c76da?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A MacBook showing a storage full warning notification",
    date: "2026-07-30",
    readTime: 6,
    tags: ["macOS", "Storage", "Cleanup"],
    relatedService: "mac-cleanup",
    blocks: [
      {
        type: "p",
        text: "You emptied Trash. Deleted a bunch of Downloads. The warning is back the next morning. Sound familiar? macOS hoards space in places most users never see. Here is what's actually taking up the room, and how to reclaim it — no third-party 'cleaner' app needed.",
      },
      { type: "h2", text: "First, see the real picture" },
      {
        type: "p",
        text: "Apple menu → About This Mac → More Info → Storage Settings. macOS shows every category with a size. Sort mentally by which ones are largest — 'System Data', 'Documents' and 'Applications' are usually the culprits.",
      },
      { type: "h2", text: "1. iCloud 'Optimize Mac Storage' is off" },
      {
        type: "p",
        text: "System Settings → iCloud → iCloud Drive → Sync this Mac → turn on 'Optimize Mac Storage'. This lets macOS move rarely-accessed files to iCloud, keeping just a placeholder locally. Instant 10–100 GB back on most Macs.",
      },
      { type: "h2", text: "2. Photos library keeping full-resolution originals" },
      {
        type: "p",
        text: "Open Photos → Settings → iCloud → 'Optimize Mac Storage'. Same idea: full-resolution stays in iCloud, small versions locally. If you have a 200 GB Photos library, this alone can free 150 GB.",
      },
      { type: "h2", text: "3. Massive 'System Data' (formerly 'Other')" },
      {
        type: "p",
        text: "This is where snapshots, caches, browser cache, iOS device backups and mail attachments live. Storage Settings → System Data can show 50–200 GB in extreme cases. Reboot first — it often halves after a proper restart because macOS purges Time Machine local snapshots.",
      },
      {
        type: "callout",
        title: "Purgeable space isn't magical.",
        text: "macOS reserves 'purgeable' space it can free automatically. If you actually need the room now (installing a large app), Storage Settings → offload or delete iOS backups, mail downloads and old Podcasts episodes to force the reclaim.",
      },
      { type: "h2", text: "4. Old iOS device backups on iTunes/Finder" },
      {
        type: "p",
        text: "If you've ever backed up an iPhone or iPad locally, those backups can be 100+ GB each. In Finder → your device → Manage Backups. Delete anything for devices you no longer own.",
      },
      { type: "h2", text: "5. Mail attachments" },
      {
        type: "p",
        text: "Mail.app downloads every attachment when you open messages. Over years this hoards tens of gigabytes at ~/Library/Mail. In Mail → Settings → Accounts → Advanced → set 'Download Attachments' to 'Recent' or 'None'.",
      },
      { type: "h2", text: "6. Docker, Xcode & other pro tools" },
      {
        type: "ul",
        items: [
          "Docker images and volumes — clean via `docker system prune -a`.",
          "Xcode DerivedData — delete ~/Library/Developer/Xcode/DerivedData.",
          "Old iOS simulators — Xcode → Settings → Platforms.",
          "Node_modules folders in old projects — combined, usually 5–20 GB.",
        ],
      },
      { type: "h2", text: "When to just upgrade the SSD" },
      {
        type: "p",
        text: "Intel Macs and older Mac Minis often have upgradeable SSDs; Apple Silicon Macs do not. If you're consistently at 90%+ full and rely on the machine, external Thunderbolt SSD (roughly $80 for 1 TB) is often smarter than fighting for gigabytes.",
      },
      {
        type: "cta",
        text: "Storage full and no obvious cause? Book a $89 remote macOS cleanup — we identify exactly what's eating your disk and reclaim it safely, without any risky 'cleaner' apps.",
      },
    ],
  },

  {
    slug: "8-settings-every-new-windows-11-laptop-owner-should-change",
    title: "8 Settings Every New Windows 11 Laptop Owner Should Change (2026)",
    description:
      "A fresh Windows 11 laptop is fast and safe — but it ships with defaults tuned for ads and telemetry, not for you. Here are the eight settings we always flip during setup.",
    cover:
      "https://images.unsplash.com/photo-1611174743420-3d7df880ce32?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A brand-new Windows 11 laptop being unboxed on a desk",
    date: "2026-08-06",
    readTime: 6,
    tags: ["Windows", "Setup", "Privacy"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "Windows 11 is a great OS out of the box, but its factory defaults quietly favour ads, telemetry and Microsoft services. Here are the eight settings we change on every new laptop we set up at Repair Point. Total time: about ten minutes.",
      },
      { type: "h2", text: "1. Turn off 'Suggestions' and Start-menu ads" },
      {
        type: "p",
        text: "Settings → Personalisation → Start → turn OFF 'Show tips, tricks and suggestions' and 'Show recommendations'. Then Settings → Privacy & security → General → turn OFF the four toggles, especially 'Let apps show me personalised ads using my advertising ID'.",
      },
      { type: "h2", text: "2. Move Start button back to the left (optional)" },
      {
        type: "p",
        text: "If the centred Start still feels wrong, Settings → Personalisation → Taskbar → Taskbar behaviours → Taskbar alignment → Left. Small thing, big daily comfort.",
      },
      { type: "h2", text: "3. Rename the PC to something you'll recognise" },
      {
        type: "p",
        text: "Settings → System → About → Rename this PC. Default names are gibberish; a friendly name (e.g., 'jane-laptop') helps with backups, remote access and network diagnostics.",
      },
      { type: "h2", text: "4. Enable BitLocker (full-disk encryption)" },
      {
        type: "p",
        text: "Control Panel → BitLocker Drive Encryption → Turn on for C: drive. Save the recovery key to your Microsoft account. This is the single biggest security setting on a laptop that might one day get lost or stolen.",
      },
      { type: "h2", text: "5. Turn on the 3-2-1-style automatic backups" },
      {
        type: "p",
        text: "Settings → Accounts → Windows Backup → turn ON OneDrive sync for Desktop, Documents and Pictures. Combine with a weekly external drive image via File History or Macrium Reflect Free.",
      },
      {
        type: "callout",
        title: "Skip the 'first-boot' bloatware pop-ups.",
        text: "Manufacturer software (McAfee trials, Norton, Wildtangent games, HP Support Assistant) rarely helps. Settings → Apps → Installed apps → uninstall anything you didn't specifically ask for. Windows Defender is enough antivirus.",
      },
      { type: "h2", text: "6. Enable clipboard history" },
      {
        type: "p",
        text: "Settings → System → Clipboard → turn ON 'Clipboard history'. Press Win + V to paste any of the last 25 things you copied. Once you use it you can't go back.",
      },
      { type: "h2", text: "7. Storage Sense" },
      {
        type: "p",
        text: "Settings → System → Storage → turn ON Storage Sense. Automatically empties the Recycle Bin, clears temp files and removes old Downloads. Prevents 'disk full' warnings without any manual maintenance.",
      },
      { type: "h2", text: "8. Set a proper power profile" },
      {
        type: "ul",
        items: [
          "Settings → System → Power & battery → Power mode: 'Balanced' when plugged in, 'Best power efficiency' on battery.",
          "Turn off automatic sleep for short breaks (Screen and sleep → set 'On battery, put my device to sleep after' to 30 minutes).",
        ],
      },
      { type: "h2", text: "Bonus: pin the four apps you actually use" },
      {
        type: "p",
        text: "Right-click each essential app in the Start menu → Pin to taskbar. Then right-click every default pinned icon you don't use (Widgets, Copilot, Microsoft Store previews) → Unpin. Your taskbar is now yours, not Microsoft's.",
      },
      {
        type: "cta",
        text: "Just bought a new Windows laptop? Book our $79 remote new-PC setup — we apply all of the above, migrate your files, and hand you back a laptop that's faster, more private and ready to work in 60 minutes.",
      },
    ],
  },

  {
    slug: "usb-c-confusion-why-your-charger-doesnt-work",
    title: "USB-C Confusion: Why Your Charger Doesn't Work (2026 Explainer)",
    description:
      "Same shape, wildly different capabilities. Here's why one USB-C cable charges your MacBook and another only trickles your phone — and how to buy the right one every time.",
    cover:
      "https://images.unsplash.com/photo-1618410320928-25228d811631?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A tangle of USB-C cables and chargers on a desk",
    date: "2026-08-13",
    readTime: 6,
    tags: ["Hardware", "Charging", "USB-C"],
    relatedService: "hardware",
    blocks: [
      {
        type: "p",
        text: "USB-C looks like one standard. It isn't. The same plug can hide a $2 phone cable or a $60 100 W Thunderbolt 4 monster — and using the wrong one is why your MacBook charges to 40% overnight or your monitor won't display 4K. Here's the plain-English map.",
      },
      { type: "h2", text: "The three things a USB-C cable can do (and often doesn't)" },
      {
        type: "ul",
        items: [
          "Power delivery (PD): from a puny 15 W trickle to 240 W full laptop-class charging.",
          "Data speed: from USB 2.0 (480 Mbps) up to Thunderbolt 4 (40 Gbps) — a 83× range.",
          "Video: some cables can carry 4K/8K displays over DisplayPort Alt Mode; many can't.",
        ],
      },
      { type: "h2", text: "Why cheap USB-C cables are the #1 culprit" },
      {
        type: "p",
        text: "A $2 cable from a petrol station is almost always USB 2.0 with no e-marker chip — meaning it caps at ~15 W. Plug it into a 96 W MacBook charger and the MacBook shows 'Not Charging' or trickle-charges overnight. The charger is fine; the cable is the bottleneck.",
      },
      { type: "h2", text: "What the wattage numbers mean" },
      {
        type: "ol",
        items: [
          "Under 20 W — phones and earbuds.",
          "20–45 W — small tablets, iPad Pro, MacBook Air on trickle.",
          "60–100 W — full MacBook Air / M-series MacBook Pro 13, 14.",
          "100–140 W — MacBook Pro 16, gaming laptops.",
          "140–240 W — high-end mobile workstations. Requires USB-C PD 3.1 (EPR).",
        ],
      },
      {
        type: "callout",
        title: "A charging brick's rated wattage is a max, not a promise.",
        text: "A 100 W wall charger only delivers 100 W if the CABLE and the DEVICE both negotiate it. Weak link wins.",
      },
      { type: "h2", text: "How to buy the right one (30-second rule)" },
      {
        type: "ol",
        items: [
          "For laptops: buy an e-marked USB-C cable rated for at least the wattage of your charger. 100 W / 5A is a safe default.",
          "For external displays or docks: look for 'DisplayPort Alt Mode' or 'Thunderbolt' explicitly on the packaging.",
          "For fast data (external SSDs): USB 3.2 Gen 2 (10 Gbps) minimum, Thunderbolt 4 if the drive is NVMe.",
          "Stick to Anker, Belkin, Cable Matters, Apple or CalDigit. Off-brand Amazon cables are hit and miss.",
        ],
      },
      { type: "h2", text: "The Thunderbolt 4 shortcut" },
      {
        type: "p",
        text: "If you never want to think about this again, buy a Thunderbolt 4 (or USB4) cable. They do everything: 40 Gbps data, 100 W charging, 8K video. Downside: shorter (usually 0.8 m) and pricier ($30–$50). Worth it for a desk cable.",
      },
      { type: "h2", text: "When to call us" },
      {
        type: "p",
        text: "'My MacBook won't charge' tickets are USB-C cable issues in about 40% of cases. If you've tried a known-good cable and the machine still won't charge, the port itself may need repair — dust, bent pins, or liquid corrosion inside. Ports are usually replaceable.",
      },
      {
        type: "cta",
        text: "Laptop not charging or ports feeling loose? Book a $49 diagnostic — we'll tell you within an hour if it's a cable, a port, or the charging IC, and quote a flat-rate fix.",
      },
    ],
  },

  {
    slug: "cheapest-upgrade-that-doubles-old-laptop-speed",
    title: "The Cheapest Upgrade That Doubles an Old Laptop's Speed",
    description:
      "Under $60 and 45 minutes will make a five-year-old Windows laptop feel like a new one. Here's the upgrade our technicians recommend more than any other — and how to know if yours qualifies.",
    cover:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "An open laptop with the back cover removed, showing internal components",
    date: "2026-08-20",
    readTime: 6,
    tags: ["Hardware", "Performance", "Upgrade"],
    relatedService: "hardware",
    blocks: [
      {
        type: "p",
        text: "Every year we get asked the same question: 'Is my laptop worth saving?' Nine times out of ten, the answer is yes — because the single cheapest upgrade in computing history is still available in 2026, and it works. Meet the SATA/NVMe SSD swap.",
      },
      { type: "h2", text: "Why SSDs make old laptops feel new" },
      {
        type: "p",
        text: "Spinning hard drives (HDDs) top out at ~100 MB/s and physically move mechanical parts to find data. Modern SSDs read at 500–7,000 MB/s with zero seek time. On a boot-and-open-Chrome test, SSDs are typically 5–15× faster than the HDD they replace. It's the single biggest speed jump in consumer computing.",
      },
      { type: "h2", text: "Does my laptop qualify?" },
      {
        type: "ol",
        items: [
          "Any Windows laptop 2015–2020 that still has a spinning HDD — 100% yes.",
          "Any laptop with an old, tiny SATA SSD (128 GB) — 1 TB upgrade both speeds it up and ends the disk-full nagging.",
          "Any 2017+ ultrabook with an NVMe SSD — swapping to a faster/larger NVMe still helps but with diminishing returns.",
          "Sealed / soldered SSDs (M1/M2/M3 MacBooks, some ultrabooks) — cannot upgrade internally; use a Thunderbolt external instead.",
        ],
      },
      { type: "h2", text: "The 2026 sweet-spot parts" },
      {
        type: "ul",
        items: [
          "2.5\" SATA SSD (for older laptops with spinning drives): Crucial MX500 1 TB — around $55.",
          "M.2 NVMe SSD (for most 2017+ laptops): Crucial P3 Plus or WD Blue SN580 1 TB — around $60.",
          "External Thunderbolt SSD (for sealed-storage Macs): OWC Envoy Pro 1 TB — around $130.",
          "Cloning software: Macrium Reflect Free (Windows) or Time Machine (Mac).",
        ],
      },
      {
        type: "callout",
        title: "One tip that saves you hours.",
        text: "Clone your OLD drive to the NEW SSD before you install it. No fresh Windows install, no re-installing apps, no reactivating licences. You boot up on the new drive and everything is exactly where you left it — just 10× faster.",
      },
      { type: "h2", text: "The upgrade at a glance" },
      {
        type: "ol",
        items: [
          "Buy the right SSD for your laptop's slot (SATA or NVMe).",
          "Buy a $10 USB-to-SATA/M.2 adapter to clone via USB.",
          "Clone the old drive to the new SSD with Macrium Reflect Free (30–60 mins).",
          "Open the laptop, swap the drives (15 mins for most models).",
          "Reboot. Marvel at the boot time.",
        ],
      },
      { type: "h2", text: "Also worth doing at the same time" },
      {
        type: "ul",
        items: [
          "Add RAM if your model allows it (4 GB → 8 GB or 8 GB → 16 GB is a huge second win, usually $30–$40).",
          "Blow the dust out of the fan and replace thermal paste while you're inside.",
          "Replace the battery if it's under 80% capacity.",
        ],
      },
      { type: "h2", text: "When to skip and just replace" },
      {
        type: "p",
        text: "If the laptop is older than 2015, has soldered RAM/SSD and only 4 GB, has a cracked hinge or dying screen — spend the money on a new machine instead. We'll always tell you honestly during the diagnostic.",
      },
      {
        type: "cta",
        text: "Old laptop feeling its age? Book a $79 SSD upgrade (parts + labour, cloning included) — turnaround under 2 hours, backed by our 30-day workmanship warranty. Same job for Macs from $89.",
      },
    ],
  },

  {
    slug: "recognise-tech-support-scam-call-30-seconds",
    title: "How to Recognise a Tech Support Scam Call in 30 Seconds",
    description:
      "Someone claiming to be from 'Microsoft' or 'Apple' just called about your infected computer. Here's the 30-second test our technicians use to spot a tech support scam — and exactly what to do if you already let them in.",
    cover:
      "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A glowing padlock icon over a dark digital background representing cyber security",
    date: "2026-08-27",
    readTime: 6,
    tags: ["Security", "Scam awareness", "Remote support"],
    relatedService: "remote-support",
    blocks: [
      {
        type: "p",
        text: "Every week at Repair Point we help someone who answered a call from 'Windows Support' or 'the Apple Security Team'. The callers are convincing, patient and well-rehearsed — but every single one of them fails the same 30-second test. Here's how to run it.",
      },
      { type: "h2", text: "The 30-second test: five instant red flags" },
      { type: "h2", text: "1. They called you" },
      {
        type: "p",
        text: "This one is absolute. Microsoft, Apple, Google and your internet provider do not cold-call customers about viruses. Neither do legitimate repair shops — including us. If the call was their idea, it ends here: hang up.",
      },
      { type: "h2", text: "2. There's sudden urgency" },
      {
        type: "p",
        text: "'Your computer will be disabled within 24 hours.' 'Hackers are on your network right now.' Real support never works on panic. Urgency exists to stop you thinking — treat it as proof of a scam, not a problem.",
      },
      { type: "h2", text: "3. They ask for remote access" },
      {
        type: "p",
        text: "AnyDesk, TeamViewer, Quick Assist, Chrome Remote Desktop — if a stranger on the phone asks you to install one, that is the attack itself. Only ever grant remote access to a technician you contacted first, at a number you verified.",
      },
      { type: "h2", text: "4. They ask for gift cards or wire transfers" },
      {
        type: "p",
        text: "No genuine company anywhere accepts payment in Apple, Google Play or Steam gift cards. The moment gift cards, crypto or a wire transfer appear, the conversation is over.",
      },
      { type: "h2", text: "5. They show you 'proof' on your own screen" },
      {
        type: "p",
        text: "Scammers love opening Event Viewer and pointing at ordinary red error icons — 'see, your PC is infected.' Every Windows PC shows thousands of these harmless entries. It is theatre, not diagnostics.",
      },
      {
        type: "callout",
        title: "A genuine company behaves differently.",
        text: "A real repair service waits for you to call, quotes a written flat-rate price before any work, accepts normal card payments with a receipt, and never objects to you hanging up and calling back on a published number.",
      },
      { type: "h2", text: "If you already let them in" },
      {
        type: "p",
        text: "Don't panic, and don't feel embarrassed — these operations are professional criminals. Act in this order:",
      },
      {
        type: "ol",
        items: [
          "Turn the computer off (hold the power button if needed) and disconnect it from Wi-Fi.",
          "If you paid by card or shared banking details, call your bank's fraud line immediately.",
          "Change your important passwords — email first, then banking — from a different, clean device.",
          "Note the caller's number and company name, and report it to the FTC at reportfraud.ftc.gov.",
          "Have the machine professionally checked for backdoors before using it again for banking or work.",
        ],
      },
      {
        type: "p",
        text: "A proper post-scam check looks for the remote-access tools they installed, new hidden user accounts, and changes to your browser and email forwarding rules — the quiet places scammers leave doors open.",
      },
      {
        type: "cta",
        text: "Received a suspicious call or let someone onto your PC? Book a $59 remote support session — we'll verify your machine is clean and show you exactly what to watch for.",
      },
    ],
  },

  {
    slug: "year-end-tech-audit-30-minute-routine-small-businesses",
    title: "Year-End Tech Audit: A 30-Minute Routine for Small Businesses",
    description:
      "Before the year closes, give every machine in your business a 30-minute once-over. Our technicians' six-step year-end audit covers updates, backups, accounts, security and hardware — no IT department required.",
    cover:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A laptop, notebook and documents arranged on a desk for a business review",
    date: "2026-09-03",
    readTime: 7,
    tags: ["Small business", "Security", "Maintenance"],
    relatedService: "pc-tuneup",
    blocks: [
      {
        type: "p",
        text: "December is when we get the same call from small businesses across the U.S.: a machine that 'was fine all year' finally gives up in the middle of invoicing season. Almost every one of those failures was visible months earlier. This is the 30-minute, per-machine routine we run for our own fleet clients — steal it.",
      },
      { type: "h2", text: "Minutes 0–5: Finish every update" },
      {
        type: "p",
        text: "Open Windows Update or System Settings → General → Software Update on a Mac, install everything pending, and restart — even if 'it never asks.' Do the same for browsers and your accounting or line-of-business apps. Most business malware we remove walks in through an update prompt somebody dismissed for eight months.",
      },
      { type: "h2", text: "Minutes 5–10: Prove your backups actually restore" },
      {
        type: "p",
        text: "A backup you've never restored from is a hope, not a backup. Pick one important folder and actually restore it to a test location. Check the last successful backup date on every machine — 'backup ran' is not the same as 'backup succeeded.'",
      },
      {
        type: "callout",
        title: "The holiday rule.",
        text: "Make sure at least one full backup completes — and one restore is tested — before the office empties for the holidays. Ransomware crews time attacks for exactly that quiet week.",
      },
      { type: "h2", text: "Minutes 10–15: Audit accounts and access" },
      {
        type: "p",
        text: "List everyone who can log in to email, banking and cloud storage. Remove anyone who left this year — this step is skipped more often than any other. Turn on multi-factor authentication anywhere it isn't already, and retire any password that's been shared around the office on a sticky note.",
      },
      { type: "h2", text: "Minutes 15–20: Check security basics" },
      {
        type: "p",
        text: "Confirm Windows Security or your business antivirus shows green on every machine, and that real-time protection is actually on — malware's first move is switching it off. Run a full scan on any machine that's been 'a bit slow' lately. Then send the team a two-line reminder: unexpected invoices and 'urgent' payment-change emails get verified by phone before anyone clicks.",
      },
      { type: "h2", text: "Minutes 20–25: Hardware health check" },
      {
        type: "p",
        text: "Check free disk space (below 10% slows everything and breaks updates), listen for grinding fans, and look at battery health on laptops. A drive that's starting to fail almost always announces itself — slow boots, clicking, corrupted files — weeks before it dies. Catching it now is a $79 tune-up; catching it after is a data recovery job.",
      },
      { type: "h2", text: "Minutes 25–30: Write it down" },
      {
        type: "p",
        text: "Update your asset register: which machines exist, their age, warranty status and what got fixed this year. It takes five minutes, it's what your accountant wants at tax time, and it turns next year's 'should we replace this?' argument into a two-minute decision.",
      },
      { type: "h2", text: "The honest shortcut" },
      {
        type: "p",
        text: "Thirty minutes per machine is genuinely all it takes — but multiplied across a team, it has a habit of never happening. That's exactly why our fleet maintenance clients hand the whole routine to us: scheduled after-hours, documented per machine, one flat monthly rate.",
      },
      {
        type: "cta",
        text: "Want the year-end audit done for you? Book a $79 tune-up per machine — or ask about our flat monthly fleet plans with proactive patching and unlimited remote support.",
      },
    ],
  },

  {
    slug: "how-to-spot-a-phishing-email-before-you-click",
    title: "How to Spot a Phishing Email Before You Click: A 60-Second Drill",
    description:
      "Phishing emails cause more infections than any virus. Here's the 60-second drill our technicians teach every customer — six checks that catch 99% of fake invoices, delivery notices and 'urgent' bank alerts.",
    cover:
      "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "Green code cascading down a dark screen representing a cyber attack",
    date: "2026-09-10",
    readTime: 6,
    tags: ["Security", "Phishing", "Email"],
    relatedService: "antivirus-setup",
    blocks: [
      {
        type: "p",
        text: "Ask our technicians what actually infects most computers and you'll get an answer that surprises people: not Hollywood hackers — email. One convincing message and one hurried click. The good news is that phishing emails almost always fail the same six checks. Run this 60-second drill before you click anything.",
      },
      { type: "h2", text: "Check 1: The real sender address" },
      {
        type: "p",
        text: "Display names mean nothing — 'Microsoft Support' can be typed by anyone. Click or tap the sender's name to reveal the actual address. Genuine PayPal mail comes from paypal.com, not paypal-secure-center.net. If the domain has extra words, hyphens or a strange ending, stop there.",
      },
      { type: "h2", text: "Check 2: Where the link really goes" },
      {
        type: "p",
        text: "Hover your mouse over any button or link (long-press on a phone) and read the preview of the true destination. A 'Track your package' button pointing to a site you've never heard of is the whole scam, visible before you click.",
      },
      { type: "h2", text: "Check 3: Manufactured urgency" },
      {
        type: "p",
        text: "'Your account will be suspended in 24 hours.' 'Unusual sign-in — verify now.' Urgency is the phisher's only real tool, because it stops you doing checks 1 and 2. Genuine companies give you weeks, not hours — and never punish you for calling them directly.",
      },
      { type: "h2", text: "Check 4: How it greets you" },
      {
        type: "p",
        text: "Your bank knows your name and uses it. 'Dear Customer' or 'Dear valued user' on a message claiming to be about YOUR account is a tell. It's not proof on its own — but combined with anything else on this list, it's decisive.",
      },
      { type: "h2", text: "Check 5: Unexpected attachments" },
      {
        type: "p",
        text: "Invoices you weren't expecting, 'scanned documents' from a printer you don't own, .zip or .html attachments of any kind — don't open them to 'see what they are'. What they are is the infection. When in doubt, reply to the sender's known address or phone them and ask.",
      },
      { type: "h2", text: "Check 6: The gut test" },
      {
        type: "p",
        text: "Refunds you didn't request, passwords expiring a year early, a CEO who suddenly emails with an urgent payment — if a message feels slightly off, it is off. Verify through a channel you already trust: the official app, the saved phone number, the website you type yourself.",
      },
      {
        type: "callout",
        title: "Already clicked? Speed matters more than panic.",
        text: "If you entered a password, change it immediately from the real site — and anywhere else you reused it. If you ran a file, disconnect from Wi-Fi and get the machine checked the same day. Most damage is preventable in the first hour.",
      },
      { type: "h2", text: "Make the drill automatic" },
      {
        type: "ol",
        items: [
          "Turn on multi-factor authentication for email and banking — it defeats most phishing even after a click.",
          "Keep your browser and OS updated so known malicious sites get blocked.",
          "Use a password manager: it refuses to autofill on lookalike domains, which is a built-in alarm.",
          "Report the message as phishing in your mail app — it trains the filter for everyone.",
        ],
      },
      {
        type: "cta",
        text: "Clicked something you shouldn't have? Book a $59 remote support session — we'll check for compromise, secure your accounts and set up protection so it can't happen twice.",
      },
    ],
  },

  {
    slug: "5-signs-your-hard-drive-is-dying",
    title: "5 Signs Your Hard Drive Is Dying (and What to Do Before It Fails)",
    description:
      "Hard drives almost never die without warning. Clicking, slow boots, vanishing files — here are the five symptoms our technicians treat as red alerts, and the exact order to act in to save your data.",
    cover:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A close-up of a computer circuit board with memory chips",
    date: "2026-09-17",
    readTime: 6,
    tags: ["Hardware", "Data recovery", "Backup"],
    relatedService: "data-recovery",
    blocks: [
      {
        type: "p",
        text: "Here's the good news buried in a scary topic: drives rarely fail silently. In our experience, most dead drives announced themselves for weeks — in clicks, freezes and missing files — before anyone listened. Learn the five signs and you can turn a data-recovery emergency into a routine backup.",
      },
      { type: "h2", text: "1. Clicking, grinding or beeping" },
      {
        type: "p",
        text: "A healthy hard drive hums quietly. Repetitive clicking (the 'click of death'), grinding or scraping means the read heads are struggling — every minute it runs can physically scrape your data off the platters. Shut the machine down; this is the one symptom where 'keep trying' actively destroys data.",
      },
      { type: "h2", text: "2. Boots that keep getting slower" },
      {
        type: "p",
        text: "If startup time creeps from 30 seconds to three minutes over a few weeks, the drive may be re-reading failing sectors. Check the drive's health: on Windows, run 'wmic diskdrive get status' or use CrystalDiskInfo; on a Mac, check Disk Utility's S.M.A.R.T. status. Anything but 'OK' or 'Verified' means act now.",
      },
      { type: "h2", text: "3. Files that vanish, corrupt or won't copy" },
      {
        type: "p",
        text: "Photos that open halfway, folders that appear empty, 'file is corrupted' errors on documents you used last week — the drive is losing the ability to read what it wrote. Stop saving new files to it; every write can overwrite recoverable data.",
      },
      { type: "h2", text: "4. Freezes and blue screens under disk load" },
      {
        type: "p",
        text: "A PC that freezes specifically when copying files, opening photos or booting — but feels fine once idle — is showing classic failing-drive behaviour. Random blue screens with storage error codes belong on the same list.",
      },
      { type: "h2", text: "5. The drive disappears" },
      {
        type: "p",
        text: "If the drive intermittently vanishes from File Explorer, Finder or even the BIOS, the electronics are on their way out. Do not format it when the computer suggests it — that prompt is the last thing your data needs.",
      },
      {
        type: "callout",
        title: "The golden rule of dying drives.",
        text: "Copy your irreplaceable files FIRST — photos, documents, work — not the whole drive image. A full clone stresses a weak drive for hours and can kill it before the important 20 GB is safe.",
      },
      { type: "h2", text: "What to do, in order" },
      {
        type: "ol",
        items: [
          "Stop using the drive for anything except copying data off it.",
          "Copy your most irreplaceable files to an external drive or cloud storage first.",
          "Check the S.M.A.R.T. health status to confirm the diagnosis.",
          "Replace the drive — an SSD swap usually doubles as a major speed upgrade.",
          "Keep the old drive untouched until you're certain everything is recovered.",
        ],
      },
      {
        type: "p",
        text: "One honest note: if the drive is clicking or won't mount at all, DIY software often makes things worse. That's the point where a professional assessment is cheaper than the data.",
      },
      {
        type: "cta",
        text: "Seeing any of these signs? Data recovery starts at $149 with a no-data-no-fee promise — or book a $79 SSD upgrade and we'll migrate everything to a healthy new drive for you.",
      },
    ],
  },

  {
    slug: "free-vs-paid-antivirus-what-you-actually-need",
    title: "Free vs Paid Antivirus in 2026: What You Actually Need",
    description:
      "Is Windows Defender enough, or should you pay for antivirus? An honest, vendor-neutral breakdown from technicians who remove malware for a living — and the two things that matter more than any AV.",
    cover:
      "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A laptop on a wooden desk beside a notebook and coffee",
    date: "2026-09-24",
    readTime: 7,
    tags: ["Security", "Antivirus"],
    relatedService: "antivirus-setup",
    blocks: [
      {
        type: "p",
        text: "We remove malware for a living, so you might expect us to sell you the most expensive antivirus on the shelf. The honest answer is more interesting: what you need depends on how you use your computer — and for many people, the best protection costs nothing.",
      },
      { type: "h2", text: "The free baseline is genuinely good now" },
      {
        type: "p",
        text: "Microsoft Defender — built into Windows 10 and 11 — scores within a few points of paid suites in independent lab tests (AV-TEST, AV-Comparatives) and adds no ads, no upsell pop-ups and no performance tax. On a Mac, XProtect and Gatekeeper handle the common threats quietly. For careful users who keep their system updated, this baseline is enough.",
      },
      { type: "h2", text: "When paying actually makes sense" },
      {
        type: "p",
        text: "Paid suites earn their money in specific situations, not universally:",
      },
      {
        type: "ul",
        items: [
          "You handle client data or run a business — you need ransomware rollback, central management and audit logs.",
          "Kids or less technical family members use the machine — web filtering and scam-site blocking catch what habits don't.",
          "You bank and shop on the same PC daily — hardened browser modes and anti-phishing layers add real friction for attackers.",
          "Multiple devices under one roof — one licence covering five to ten devices is cheaper than per-machine free tools with gaps.",
        ],
      },
      { type: "h2", text: "What to ignore completely" },
      {
        type: "p",
        text: "Ignore 'your PC is infected!' pop-ups telling you to buy a cleaner — real antivirus never advertises through scare banners. Ignore benchmark charts on vendor websites. And ignore any suite that slows your machine noticeably in the first week; security that punishes you gets switched off, which is worse than none.",
      },
      {
        type: "callout",
        title: "The uncomfortable truth.",
        text: "The two protections that stop most real-world infections cost nothing: automatic updates turned on, and multi-factor authentication on your email. We've removed malware from machines running $80 security suites that had both disabled.",
      },
      { type: "h2", text: "Our vendor-neutral shortlist" },
      {
        type: "ol",
        items: [
          "Careful home user, Windows: Microsoft Defender + updates on + MFA. Spend $0.",
          "Family PC with mixed users: a reputable paid suite with web protection — Bitdefender, ESET or similar mid-tier, around $40–60/year.",
          "Small business: business-grade endpoint protection with central reporting — this is the one category where free is false economy.",
          "Mac users: built-in protections plus a reputable on-demand scanner for occasional second opinions.",
        ],
      },
      { type: "h2", text: "One antivirus, not three" },
      {
        type: "p",
        text: "A final note from the repair bench: a surprising share of the 'slow PCs' we fix are simply running two or three security products fighting each other. If a machine came with a trial suite, expired AV and a 'PC optimizer', remove all but one. The cleanup alone often restores the speed.",
      },
      {
        type: "cta",
        text: "Not sure what your PC needs? Book an antivirus consultation — vendor-neutral advice, clean install and tuning, and safe removal of expired or duplicate security software. Flat $49 diagnostic, waived if you proceed.",
      },
    ],
  },

  {
    slug: "selling-or-donating-an-old-computer-do-these-6-things-first",
    title: "Selling or Donating an Old Computer? Do These 6 Things First",
    description:
      "A quick factory reset is not enough — deleted files are recoverable for years. Before your old PC or Mac changes hands, run this six-step wipe-and-prep checklist from our data security technicians.",
    cover:
      "https://images.unsplash.com/photo-1550009158-9ebf69173e03?auto=format&fit=crop&w=1600&q=70",
    coverAlt: "A collection of older electronic devices and gadgets on a table",
    date: "2026-10-01",
    readTime: 6,
    tags: ["Privacy", "Security", "Windows", "macOS"],
    relatedService: "remote-support",
    blocks: [
      {
        type: "p",
        text: "Every month, someone brings us a laptop they bought second-hand — and asks why it still contains the previous owner's tax returns. Deleting files, emptying the recycle bin and even a standard factory reset leave recoverable traces. If your old machine is leaving the house, do these six things in order.",
      },
      { type: "h2", text: "1. Back up what you're keeping — then verify it" },
      {
        type: "p",
        text: "Copy documents, photos, browser bookmarks and password exports to your new machine or cloud storage. Then actually open a few files there. A backup you haven't spot-checked is a guess.",
      },
      { type: "h2", text: "2. Sign out of everything that limits devices" },
      {
        type: "p",
        text: "iCloud/Find My Mac, Microsoft account, Google, Adobe Creative Cloud and iTunes/Music all tie a licence or activation lock to the machine. Sign out and deauthorize first — activation lock left on makes the computer useless to the next owner and stalls the sale.",
      },
      { type: "h2", text: "3. Encrypt, then wipe — not the other way round" },
      {
        type: "p",
        text: "Turn on device encryption first (BitLocker on Windows Pro, FileVault on Mac — most modern PCs have it on already), then reset. Wiping an encrypted drive destroys the key, making leftover data mathematically unreadable. This single step is the difference between 'deleted' and 'gone'.",
      },
      { type: "h2", text: "4. Use the secure erase path for your drive type" },
      {
        type: "p",
        text: "Windows: Settings → System → Recovery → Reset this PC → Remove everything → Clean data: Yes. Mac (Apple Silicon or T2): Erase All Content and Settings. Older Intel Macs with spinning drives: Disk Utility's Security Options. For SSDs, never use multi-pass overwrite tools — the built-in erase is both safer for the drive and, with encryption on, just as final.",
      },
      { type: "h2", text: "5. Remove it from your account dashboards" },
      {
        type: "p",
        text: "After the wipe, sign in to your Microsoft, Apple and Google accounts from your NEW device and remove the old one from the trusted-device lists. Five minutes here prevents the strange sign-in alerts people get months after a sale.",
      },
      { type: "h2", text: "6. Do a physical once-over" },
      {
        type: "p",
        text: "Check the SD card slot, DVD tray and USB ports for forgotten media. Wipe the machine down, include the charger (chargers sell machines), and note the serial number for your records before it leaves.",
      },
      {
        type: "callout",
        title: "Donating to a charity or school?",
        text: "Ask whether they refurbish or resell through third parties. Reputable organisations wipe to a recognised standard — but a machine you've already encrypted and reset yourself is safe either way.",
      },
      {
        type: "cta",
        text: "Want it done properly without the homework? Book a $59 remote support session — we'll verify your backup, handle the encryption-and-wipe and set up your new machine in the same visit.",
      },
    ],
  },
];

export function getPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug);
}

export function getRelatedPosts(slug, n = 2) {
  return POSTS.filter((p) => p.slug !== slug).slice(0, n);
}
